import type { Dictionary } from "./en";

type Deep<T> = { [K in keyof T]?: T[K] extends readonly unknown[] ? T[K] : Deep<T[K]> };

/** Simplified Chinese */
export const zhHans: Deep<Dictionary> = {
  meta: {
    title: "Revolin Roofing | 大温哥华地区高端屋顶工程",
    description:
      "为大温哥华地区业主提供屋顶更换、维修与高端屋顶系统。详尽评估、透明报价、Revolin 集团标准。",
  },
  nav: {
    services: "服务项目",
    roofTypes: "屋顶类型",
    beforeAfter: "施工前后",
    areas: "服务区域",
    why: "选择 Revolin",
    faq: "常见问题",
    contact: "联系我们",
    menu: "菜单",
    close: "关闭菜单",
    language: "语言",
  },
  cta: {
    primary: "预约屋顶评估",
    call: "立即致电",
    callBrand: "致电 Revolin Roofing",
    learnMore: "了解更多",
  },
  hero: {
    eyebrow: "大温哥华 · 低陆平原",
    titleLine1: "为卑诗气候而建的屋顶。",
    titleLine2: "以 Revolin 标准完成。",
    body: "Revolin Roofing 为大温哥华业主提供屋顶更换、屋顶维修和高端屋顶方案，并根据住宅、屋顶状况及适用要求提出建议。",
    imageCaption: "北岸建筑的直立锁边金属屋顶",
  },
  trust: {
    eyebrow: "保障与资质",
    title: "投保、注册与质保工程",
    guaranteeMention: "每项工程均提供书面工艺质保。完整保证说明见关于页面。",
    guaranteeLink: "我们的保证",
  },
  brands: {
    eyebrow: "材料",
    title: "我们安装可信品牌",
    seeAll: "查看我们信赖的材料",
  },
  testimonials: {
    eyebrow: "评价",
    title: "业主怎么说",
    previewNotice: "预览占位内容：生产环境不显示",
    googleCta: "在 Google 上阅读更多评价：",
    googleLinkLabel: "Google 评价",
  },
  services: {
    eyebrow: "服务项目",
    title: "大温哥华住宅屋顶服务",
    body: "从紧急漏水到完整的建筑屋顶重铺，每个项目都从同样严谨的评估开始。",
    items: [
      {
        title: "屋顶更换",
        intent: "我的屋顶已接近使用寿命末期",
        body: "拆除现有屋面并重铺，采用适合您住宅的系统、垫层、通风和泛水细部。",
      },
      {
        title: "屋顶维修与漏水诊断",
        intent: "我的屋顶漏水或有可见损坏",
        body: "提出维修范围前，会评估泛水、穿透点和排水等常见漏水路径。",
      },
      {
        title: "建筑型沥青瓦",
        intent: "我希望采用成熟、常见的屋顶方案",
        body: "适用于坡屋顶的可靠住宅方案，提供丰富的轮廓和颜色选择。",
      },
      {
        title: "直立锁边金属屋顶",
        intent: "我重视耐久性和现代外观",
        body: "适合现代及西海岸建筑的高端方案，采用隐藏式固定，线条利落。",
      },
      {
        title: "平屋顶与低坡屋顶",
        intent: "我的屋顶平坦或坡度较低",
        body: "根据排水、坡度以及女儿墙、基座和交接处的细部要求指定膜材系统。",
      },
      {
        title: "屋顶检查与维护",
        intent: "我想了解屋顶目前的状况",
        body: "记录屋顶状况并确定维护优先事项，为审慎规划提供依据。",
      },
    ],
  },
  comparison: {
    eyebrow: "屋顶系统",
    title: "哪种屋顶适合您的住宅？",
    body: "没有一种屋顶材料适合所有住宅。合适的系统取决于坡度、建筑设计、暴露条件、排水、预算及适用的当地要求。以下是主要方案的一般比较。",
    columns: {
      application: "理想用途",
      character: "设计特色",
      maintenance: "维护考量",
      investment: "相对投入",
      performance: "性能特点",
    },
    systems: [
      {
        name: "建筑型沥青瓦",
        summary: "成熟可靠的常见住宅方案。",
        application: "低陆平原大多数住宅的坡屋顶。",
        character: "立体质感，适合传统至过渡型风格。",
        maintenance: "定期检查；沿海气候下需要重视苔藓控制。",
        investment: "中等",
        performance: "成熟易懂的系统，产品供应广泛，日后维修较为直接。",
      },
      {
        name: "直立锁边金属屋顶",
        summary: "兼顾耐久性与设计的高端方案。",
        application: "屋面线条简洁的现代及西海岸建筑。",
        character: "利落、线性、富有建筑感。",
        maintenance: "日常维护较少；细部设计和固定质量至关重要。",
        investment: "较高",
        performance: "隐藏式固定和连续板材能在暴露立面有效排水。",
      },
      {
        name: "平屋顶与低坡膜材系统",
        summary: "适用于低坡区域的系统类别。",
        application: "平屋顶、低坡屋面、露台和扩建部分。",
        character: "低调内敛：屋顶并非主要视觉元素。",
        maintenance: "检查排水口和接缝；保持排水路径畅通。",
        investment: "因膜材和系统而异",
        performance: "性能很大程度取决于排水坡度、膜材选择和交接细部。",
      },
    ],
    disclaimer:
      "仅供一般参考。材料选择和系统细部应遵循现行 RCABC 指引以及适用的卑诗省和市政要求。我们不提供工程保证。",
  },
  why: {
    eyebrow: "为什么选择 Revolin Roofing",
    title: "建筑公司的专业屋顶部门",
    body: "Revolin Roofing 将 Revolin Group 的项目方法用于住宅屋顶：先评估、提供拟议书面工程范围，并重视沿海天气相关细部。",
    items: [
      { title: "先评估，再建议", body: "提出工程范围前，我们会记录检查结果。" },
      {
        title: "透明的范围和价格",
        body: "拟议范围会说明包含项、排除项，以及可能需要变更的状况。",
      },
      { title: "注重建筑设计", body: "屋顶线条、颜色和轮廓会根据建筑选择。" },
      {
        title: "泛水和交接细部",
        body: "交接部位是常见检查重点，因此在评估和确定范围时会得到审慎关注。",
      },
      { title: "现场规划", body: "根据具体物业确定进场、保护和清理要求。" },
      { title: "项目沟通", body: "工期和沟通要求会随项目工程范围一并确认。" },
      { title: "质量控制节点", body: "在安装过程中及完工时进行明确检查。" },
      {
        title: "母公司专业支持",
        body: "当屋顶问题不只是屋顶本身时，可借助 Revolin Group 更广泛的施工能力。",
      },
    ],
  },
  beforeAfter: {
    eyebrow: "施工前后",
    title: "从细部看出差别",
    body: "拖动滑块，比较大温哥华住宅屋顶的施工前后变化。",
    beforeLabel: "施工前",
    afterLabel: "施工后",
    sliderLabel: "施工前后对比滑块",
  },
  process: {
    eyebrow: "流程",
    title: "Revolin 屋顶项目如何进行",
    steps: [
      {
        n: "01",
        title: "屋顶评估",
        body: "我们检查屋顶、泛水、排水和室内迹象，并记录状况。",
      },
      {
        n: "02",
        title: "工程范围与屋顶建议",
        body: "提案可记录合适的方案、理由、工程范围和价格，供您审阅。",
      },
      {
        n: "03",
        title: "安装／维修",
        body: "获批工程会依照已记录的范围、现场计划和适用项目要求进行。",
      },
      {
        n: "04",
        title: "最终检查与交接",
        body: "完工要求和任何项目文件均以约定工程范围为准。",
      },
    ],
  },
  weather: {
    eyebrow: "卑诗省环境",
    title: "这里的屋顶是一套水管理系统",
    body: "在低陆平原，屋顶一年中有很长时间需要应对持续降雨，而非阳光。这会改变需要重视的事项。",
    items: [
      { title: "持续降雨和湿气", body: "在潮湿的沿海气候下，垫层、搭接和通风更为重要。" },
      { title: "风和暴露条件", body: "暴露立面和开阔场地会影响固定方式及边缘细部。" },
      { title: "排水", body: "排水坡度、檐沟和落水管容量决定大量雨水的去向。" },
      {
        title: "适用地区的降雪",
        body: "本区海拔较高的地方会承受平坦低地没有的雪荷载和融雪循环。",
      },
      {
        title: "屋顶几何形态",
        body: "屋谷、老虎窗、天窗和扩建部分会形成最需要谨慎处理的交接。",
      },
    ],
  },
  areas: {
    eyebrow: "服务区域",
    title: "服务大温哥华和低陆平原",
    body: "如果您的社区在附近但未列出，请联系我们确认能否提供服务。",
  },
  faq: {
    eyebrow: "常见问题",
    title: "业主常见问题",
    items: [
      {
        q: "我的屋顶需要维修还是整体更换？",
        a: "这取决于屋顶系统的年限和状况，以及问题是否局部。局部问题可能适合维修，而较旧的覆盖层或多个失效点可能值得考虑更换。评估有助于记录哪些方案符合观察到的状况。",
      },
      {
        q: "我的屋顶正在漏水，首先该怎么办？",
        a: "先在室内控制进水，在安全的情况下拍摄可见状况，然后联系我们。不要爬上湿滑屋顶。漏点经常远离可见水渍，因此诊断比简单修补更重要。",
      },
      {
        q: "屋顶评估包括哪些内容？",
        a: "我们会检查屋面覆盖层、泛水、穿透点、通风和排水，在相关情况下查看室内迹象，并记录结果。随后您会收到书面工程范围和建议。",
      },
      {
        q: "哪种屋顶材料最好？",
        a: "没有一种材料适合所有住宅。坡度、建筑设计、暴露条件、排水、预算和适用的当地要求都会影响选择。我们会说明取舍并建议完整系统，而不是推销单一产品。",
      },
      {
        q: "你们可以处理平屋顶和低坡屋顶吗？",
        a: "可以。低坡区域会采用膜材系统，尤其重视排水坡度以及女儿墙、基座和交接处的细部。",
      },
      {
        q: "金属屋顶还是沥青瓦？",
        a: "建筑型沥青瓦是成熟可靠的常见住宅方案。直立锁边金属则是兼顾耐久性与设计的高端方案，适合现代及西海岸建筑。两者都可能正确，取决于建筑和您的优先事项。",
      },
      {
        q: "屋顶项目需要多长时间？",
        a: "工期取决于面积、复杂程度、进场条件和所选系统。我们会随工程范围提供预计工期，并在状况变化时向您更新。",
      },
      {
        q: "如何获得报价？",
        a: "请申请屋顶评估。我们会查看屋顶，然后提供书面工程范围和价格。未查看屋顶前，我们不会为重铺工程报价。",
      },
      {
        q: "施工期间下雨怎么办？",
        a: "天气会影响安全施工、工序和进度。保护措施及任何天气相关变更，会通过项目专属计划和沟通流程处理。",
      },
      {
        q: "适用什么保修？",
        a: "制造商保障因产品而异，施工保修条款会在书面工程范围中确认。我们不会在此公布保修条款；适用于您项目的条款以项目文件所载为准。",
      },
      {
        q: "你们服务哪些区域？",
        a: "我们服务大温哥华和低陆平原，包括本页所列社区。请联系我们确认附近地区的服务安排。",
      },
    ],
  },
  form: {
    eyebrow: "申请评估",
    title: "申请屋顶评估",
    body: "请告诉我们物业、屋顶以及您观察到的情况。",
    name: "姓名",
    phone: "电话",
    email: "电子邮件",
    postal: "邮政编码",
    need: "屋顶需求",
    needOptions: ["屋顶更换", "屋顶维修／漏水", "平屋顶或低坡屋顶", "屋顶检查", "暂不确定"],
    timeline: "期望时间",
    timelineOptions: ["尽快", "1–3 个月内", "3–6 个月内", "仅在规划阶段"],
    message: "描述问题（可选）",
    submit: "预约屋顶评估",
    submitting: "正在发送…",
    successTitle: "已收到申请",
    successBody: "谢谢。您的屋顶评估申请已成功送达。",
    errorRequired: "此项为必填项",
    errorEmail: "请输入有效的电子邮件地址",
    errorPhone: "请输入有效的电话号码",
    errorSubmit: "您的请求未能发送，请使用本页的联系方式与我们联系。",
    errorUnavailable: "在线申请暂时不可用，请使用本页的联系方式与我们联系。",
    privacy: "提交即表示您同意我们就屋顶咨询与您联系。",
    privacyLink: "隐私政策",
  },
  finalCta: {
    title: "屋顶守护着它下面的一切。",
    body: "从一次评估开始。您将清楚了解屋顶现状、可选方案以及各自的施工内容。",
  },
  footer: {
    servicesTitle: "服务项目",
    areasTitle: "服务区域",
    contactTitle: "联系方式",
    privacy: "隐私政策",
    terms: "使用条款",
    rights: "版权所有。",
    disclaimer: "本网站的屋顶信息仅供一般参考，并非工程意见。建议将在现场评估后确认。",
  },
  sticky: { call: "致电", quote: "索取报价" },
};

/** Traditional Chinese */
export const zhHant: Deep<Dictionary> = {
  meta: {
    title: "Revolin Roofing | 大溫哥華地區高端屋頂工程",
    description:
      "為大溫哥華地區業主提供屋頂更換、維修與高端屋頂系統。詳盡評估、透明報價、Revolin 集團標準。",
  },
  nav: {
    services: "服務項目",
    roofTypes: "屋頂類型",
    beforeAfter: "施工前後",
    areas: "服務區域",
    why: "選擇 Revolin",
    faq: "常見問題",
    contact: "聯絡我們",
    menu: "選單",
    close: "關閉選單",
    language: "語言",
  },
  cta: {
    primary: "預約屋頂評估",
    call: "立即致電",
    callBrand: "致電 Revolin Roofing",
    learnMore: "了解更多",
  },
  hero: {
    eyebrow: "大溫哥華 · 低陸平原",
    titleLine1: "為卑詩氣候而建的屋頂。",
    titleLine2: "以 Revolin 標準完成。",
    body: "Revolin Roofing 為大溫哥華業主提供屋頂更換、屋頂維修和高端屋頂方案，並根據住宅、屋頂狀況及適用要求提出建議。",
    imageCaption: "北岸建築的直立鎖邊金屬屋頂",
  },
  trust: {
    eyebrow: "保障與資質",
    title: "投保、註冊與保固工程",
    guaranteeMention: "每項工程均提供書面工藝保固。完整保證說明見關於頁面。",
    guaranteeLink: "我們的保證",
  },
  brands: {
    eyebrow: "材料",
    title: "我們安裝可信品牌",
    seeAll: "查看我們信賴的材料",
  },
  testimonials: {
    eyebrow: "評價",
    title: "屋主怎麼說",
    previewNotice: "預覽佔位內容：正式環境不顯示",
    googleCta: "在 Google 上閱讀更多評價：",
    googleLinkLabel: "Google 評價",
  },
  services: {
    eyebrow: "服務項目",
    title: "大溫哥華住宅屋頂服務",
    body: "從緊急漏水到完整的建築屋頂重鋪，每個專案都從同樣嚴謹的評估開始。",
    items: [
      {
        title: "屋頂更換",
        intent: "我的屋頂已接近使用壽命末期",
        body: "拆除現有屋面並重鋪，採用適合您住宅的系統、墊層、通風和泛水細部。",
      },
      {
        title: "屋頂維修與漏水診斷",
        intent: "我的屋頂漏水或有可見損壞",
        body: "提出維修範圍前，會評估泛水、穿透點和排水等常見漏水路徑。",
      },
      {
        title: "建築型瀝青瓦",
        intent: "我希望採用成熟、常見的屋頂方案",
        body: "適用於坡屋頂的可靠住宅方案，提供豐富的輪廓和顏色選擇。",
      },
      {
        title: "直立鎖邊金屬屋頂",
        intent: "我重視耐久性和現代外觀",
        body: "適合現代及西海岸建築的高端方案，採用隱藏式固定，線條利落。",
      },
      {
        title: "平屋頂與低坡屋頂",
        intent: "我的屋頂平坦或坡度較低",
        body: "根據排水、坡度以及女兒牆、基座和交接處的細部要求指定膜材系統。",
      },
      {
        title: "屋頂檢查與維護",
        intent: "我想了解屋頂目前的狀況",
        body: "記錄屋頂狀況並確定維護優先事項，為審慎規劃提供依據。",
      },
    ],
  },
  comparison: {
    eyebrow: "屋頂系統",
    title: "哪種屋頂適合您的住宅？",
    body: "沒有一種屋頂材料適合所有住宅。合適的系統取決於坡度、建築設計、暴露條件、排水、預算及適用的當地要求。以下是主要方案的一般比較。",
    columns: {
      application: "理想用途",
      character: "設計特色",
      maintenance: "維護考量",
      investment: "相對投入",
      performance: "性能特點",
    },
    systems: [
      {
        name: "建築型瀝青瓦",
        summary: "成熟可靠的常見住宅方案。",
        application: "低陸平原大多數住宅的坡屋頂。",
        character: "立體質感，適合傳統至過渡型風格。",
        maintenance: "定期檢查；沿海氣候下需要重視苔蘚控制。",
        investment: "中等",
        performance: "成熟易懂的系統，產品供應廣泛，日後維修較為直接。",
      },
      {
        name: "直立鎖邊金屬屋頂",
        summary: "兼顧耐久性與設計的高端方案。",
        application: "屋面線條簡潔的現代及西海岸建築。",
        character: "利落、線性、富有建築感。",
        maintenance: "日常維護較少；細部設計和固定品質至關重要。",
        investment: "較高",
        performance: "隱藏式固定和連續板材能在暴露立面有效排水。",
      },
      {
        name: "平屋頂與低坡膜材系統",
        summary: "適用於低坡區域的系統類別。",
        application: "平屋頂、低坡屋面、露臺和擴建部分。",
        character: "低調內斂：屋頂並非主要視覺元素。",
        maintenance: "檢查排水口和接縫；保持排水路徑暢通。",
        investment: "因膜材和系統而異",
        performance: "性能很大程度取決於排水坡度、膜材選擇和交接細部。",
      },
    ],
    disclaimer:
      "僅供一般參考。材料選擇和系統細部應遵循現行 RCABC 指引以及適用的卑詩省和市政要求。我們不提供工程保證。",
  },
  why: {
    eyebrow: "為什麼選擇 Revolin Roofing",
    title: "建築公司的專業屋頂部門",
    body: "Revolin Roofing 將 Revolin Group 的專案方法用於住宅屋頂：先評估、提供擬議書面工程範圍，並重視沿海天氣相關細部。",
    items: [
      { title: "先評估，再建議", body: "提出工程範圍前，我們會記錄檢查結果。" },
      {
        title: "透明的範圍和價格",
        body: "擬議範圍會說明包含項、排除項，以及可能需要變更的狀況。",
      },
      { title: "注重建築設計", body: "屋頂線條、顏色和輪廓會根據建築選擇。" },
      {
        title: "泛水和交接細部",
        body: "交接部位是常見檢查重點，因此在評估和確定範圍時會得到審慎關注。",
      },
      { title: "現場規劃", body: "根據具體物業確定進場、保護和清理要求。" },
      { title: "專案溝通", body: "工期和溝通要求會隨專案工程範圍一併確認。" },
      { title: "品質控制節點", body: "在安裝過程中及完工時進行明確檢查。" },
      {
        title: "母公司專業支持",
        body: "當屋頂問題不只是屋頂本身時，可藉助 Revolin Group 更廣泛的施工能力。",
      },
    ],
  },
  beforeAfter: {
    eyebrow: "施工前後",
    title: "從細部看出差別",
    body: "拖動滑塊，比較大溫哥華住宅屋頂的施工前後變化。",
    beforeLabel: "施工前",
    afterLabel: "施工後",
    sliderLabel: "施工前後對比滑塊",
  },
  process: {
    eyebrow: "流程",
    title: "Revolin 屋頂專案如何進行",
    steps: [
      {
        n: "01",
        title: "屋頂評估",
        body: "我們檢查屋頂、泛水、排水和室內跡象，並記錄狀況。",
      },
      {
        n: "02",
        title: "工程範圍與屋頂建議",
        body: "提案可記錄合適的方案、理由、工程範圍和價格，供您審閱。",
      },
      {
        n: "03",
        title: "安裝／維修",
        body: "獲批工程會依照已記錄的範圍、現場計劃和適用專案要求進行。",
      },
      {
        n: "04",
        title: "最終檢查與交接",
        body: "完工要求和任何專案文件均以約定工程範圍為準。",
      },
    ],
  },
  weather: {
    eyebrow: "卑詩省環境",
    title: "這裡的屋頂是一套水管理系統",
    body: "在低陸平原，屋頂一年中有很長時間需要應對持續降雨，而非陽光。這會改變需要重視的事項。",
    items: [
      { title: "持續降雨和濕氣", body: "在潮濕的沿海氣候下，墊層、搭接和通風更為重要。" },
      { title: "風和暴露條件", body: "暴露立面和開闊場地會影響固定方式及邊緣細部。" },
      { title: "排水", body: "排水坡度、檐溝和落水管容量決定大量雨水的去向。" },
      {
        title: "適用地區的降雪",
        body: "本區海拔較高的地方會承受平坦低地沒有的雪荷載和融雪循環。",
      },
      {
        title: "屋頂幾何形態",
        body: "屋谷、老虎窗、天窗和擴建部分會形成最需要謹慎處理的交接。",
      },
    ],
  },
  areas: {
    eyebrow: "服務區域",
    title: "服務大溫哥華和低陸平原",
    body: "如果您的社區在附近但未列出，請聯絡我們確認能否提供服務。",
  },
  faq: {
    eyebrow: "常見問題",
    title: "業主常見問題",
    items: [
      {
        q: "我的屋頂需要維修還是整體更換？",
        a: "這取決於屋頂系統的年限和狀況，以及問題是否局部。局部問題可能適合維修，而較舊的覆蓋層或多個失效點可能值得考慮更換。評估有助於記錄哪些方案符合觀察到的狀況。",
      },
      {
        q: "我的屋頂正在漏水，首先該怎麼辦？",
        a: "先在室內控制進水，在安全的情況下拍攝可見狀況，然後聯絡我們。不要爬上濕滑屋頂。漏點經常遠離可見水漬，因此診斷比簡單修補更重要。",
      },
      {
        q: "屋頂評估包括哪些內容？",
        a: "我們會檢查屋面覆蓋層、泛水、穿透點、通風和排水，在相關情況下查看室內跡象，並記錄結果。隨後您會收到書面工程範圍和建議。",
      },
      {
        q: "哪種屋頂材料最好？",
        a: "沒有一種材料適合所有住宅。坡度、建築設計、暴露條件、排水、預算和適用的當地要求都會影響選擇。我們會說明取捨並建議完整系統，而不是推銷單一產品。",
      },
      {
        q: "你們可以處理平屋頂和低坡屋頂嗎？",
        a: "可以。低坡區域會採用膜材系統，尤其重視排水坡度以及女兒牆、基座和交接處的細部。",
      },
      {
        q: "金屬屋頂還是瀝青瓦？",
        a: "建築型瀝青瓦是成熟可靠的常見住宅方案。直立鎖邊金屬則是兼顧耐久性與設計的高端方案，適合現代及西海岸建築。兩者都可能正確，取決於建築和您的優先事項。",
      },
      {
        q: "屋頂專案需要多長時間？",
        a: "工期取決於面積、複雜程度、進場條件和所選系統。我們會隨工程範圍提供預計工期，並在狀況變化時向您更新。",
      },
      {
        q: "如何獲得報價？",
        a: "請申請屋頂評估。我們會查看屋頂，然後提供書面工程範圍和價格。未查看屋頂前，我們不會為重鋪工程報價。",
      },
      {
        q: "施工期間下雨怎麼辦？",
        a: "天氣會影響安全施工、工序和進度。保護措施及任何天氣相關變更，會通過專案專屬計劃和溝通流程處理。",
      },
      {
        q: "適用什麼保固？",
        a: "製造商保固因產品而異，施工保固條款會在書面工程範圍中確認。我們不會在此公布保固條款；適用於您專案的條款以專案文件所載為準。",
      },
      {
        q: "你們服務哪些區域？",
        a: "我們服務大溫哥華和低陸平原，包括本頁所列社區。請聯絡我們確認附近地區的服務安排。",
      },
    ],
  },
  form: {
    eyebrow: "申請評估",
    title: "申請屋頂評估",
    body: "請告訴我們物業、屋頂以及您觀察到的情況。",
    name: "姓名",
    phone: "電話",
    email: "電子郵件",
    postal: "郵遞區號",
    need: "屋頂需求",
    needOptions: ["屋頂更換", "屋頂維修／漏水", "平屋頂或低坡屋頂", "屋頂檢查", "暫不確定"],
    timeline: "期望時間",
    timelineOptions: ["儘快", "1–3 個月內", "3–6 個月內", "僅在規劃階段"],
    message: "描述問題（可選）",
    submit: "預約屋頂評估",
    submitting: "正在傳送…",
    successTitle: "已收到申請",
    successBody: "謝謝。您的屋頂評估申請已成功送達。",
    errorRequired: "此項為必填項",
    errorEmail: "請輸入有效的電子郵件地址",
    errorPhone: "請輸入有效的電話號碼",
    errorSubmit: "您的申請未能傳送，請使用本頁的聯絡方式與我們聯絡。",
    errorUnavailable: "線上申請暫時無法使用，請使用本頁的聯絡方式與我們聯絡。",
    privacy: "提交即表示您同意我們就屋頂諮詢與您聯絡。",
    privacyLink: "隱私權政策",
  },
  finalCta: {
    title: "屋頂守護著它下面的一切。",
    body: "從一次評估開始。您將清楚了解屋頂現況、可選方案以及各自的施工內容。",
  },
  footer: {
    servicesTitle: "服務項目",
    areasTitle: "服務區域",
    contactTitle: "聯絡方式",
    privacy: "隱私權政策",
    terms: "使用條款",
    rights: "版權所有。",
    disclaimer: "本網站的屋頂資訊僅供一般參考，並非工程意見。建議將在現場評估後確認。",
  },
  sticky: { call: "致電", quote: "索取報價" },
};

/** Punjabi (Gurmukhi) */
export const pa: Deep<Dictionary> = {
  meta: {
    title: "Revolin Roofing | ਮੈਟਰੋ ਵੈਨਕੂਵਰ ਵਿੱਚ ਪ੍ਰੀਮੀਅਮ ਛੱਤ ਸੇਵਾਵਾਂ",
    description: "ਮੈਟਰੋ ਵੈਨਕੂਵਰ ਦੇ ਘਰ ਮਾਲਕਾਂ ਲਈ ਛੱਤ ਬਦਲਣਾ, ਮੁਰੰਮਤ ਅਤੇ ਪ੍ਰੀਮੀਅਮ ਰੂਫਿੰਗ ਸਿਸਟਮ।",
  },
  nav: {
    services: "ਸੇਵਾਵਾਂ",
    roofTypes: "ਛੱਤ ਦੀਆਂ ਕਿਸਮਾਂ",
    beforeAfter: "ਪਹਿਲਾਂ ਤੇ ਬਾਅਦ",
    areas: "ਸੇਵਾ ਖੇਤਰ",
    why: "ਕਿਉਂ Revolin",
    faq: "ਸਵਾਲ-ਜਵਾਬ",
    contact: "ਸੰਪਰਕ",
    menu: "ਮੀਨੂ",
    close: "ਮੀਨੂ ਬੰਦ ਕਰੋ",
    language: "ਭਾਸ਼ਾ",
  },
  cta: {
    primary: "ਛੱਤ ਦੀ ਜਾਂਚ ਬੁੱਕ ਕਰੋ",
    call: "ਹੁਣੇ ਕਾਲ ਕਰੋ",
    callBrand: "Revolin Roofing ਨੂੰ ਕਾਲ ਕਰੋ",
    learnMore: "ਹੋਰ ਜਾਣੋ",
  },
  hero: {
    eyebrow: "ਮੈਟਰੋ ਵੈਨਕੂਵਰ · ਲੋਅਰ ਮੇਨਲੈਂਡ",
    titleLine1: "BC ਲਈ ਬਣਾਈਆਂ ਛੱਤਾਂ।",
    titleLine2: "Revolin ਮਿਆਰਾਂ ਨਾਲ ਪੂਰੀਆਂ।",
    body: "Revolin Roofing ਮੈਟਰੋ ਵੈਨਕੂਵਰ ਦੇ ਘਰ ਮਾਲਕਾਂ ਲਈ ਛੱਤ ਬਦਲਣ, ਮੁਰੰਮਤ ਅਤੇ ਪ੍ਰੀਮੀਅਮ ਰੂਫਿੰਗ ਸਿਸਟਮ ਪ੍ਰਦਾਨ ਕਰਦੀ ਹੈ।",
    imageCaption: "ਸਟੈਂਡਿੰਗ-ਸੀਮ ਮੈਟਲ ਛੱਤ, ਨੌਰਥ ਸ਼ੋਰ ਆਰਕੀਟੈਕਚਰ",
  },
  trust: {
    eyebrow: "ਕਵਰੇਜ ਅਤੇ ਪ੍ਰਮਾਣ-ਪੱਤਰ",
    title: "ਬੀਮਾ, ਰਜਿਸਟਰਡ ਅਤੇ ਵਾਰੰਟੀ ਵਾਲਾ ਕੰਮ",
    guaranteeMention:
      "ਹਰ ਨੌਕਰੀ ਉੱਤੇ ਕਾਰੀਗਰੀ ਕਵਰੇਜ ਲਿਖਤੀ ਰੂਪ ਵਿੱਚ ਜਾਰੀ ਹੁੰਦੀ ਹੈ। ਪੂਰੀ ਗਾਰੰਟੀ ਵੇਰਵੇ ਬਾਰੇ ਪੰਨੇ ਉੱਤੇ ਪੜ੍ਹੋ।",
    guaranteeLink: "ਸਾਡੀ ਗਾਰੰਟੀ",
  },
  brands: {
    eyebrow: "ਸਮੱਗਰੀ",
    title: "ਅਸੀਂ ਭਰੋਸੇਯੋਗ ਬ੍ਰਾਂਡ ਲਗਾਉਂਦੇ ਹਾਂ",
    seeAll: "ਸਾਡੀਆਂ ਭਰੋਸੇਯੋਗ ਸਮੱਗਰੀਆਂ ਵੇਖੋ",
  },
  testimonials: {
    eyebrow: "ਸਮੀਖਿਆਵਾਂ",
    title: "ਘਰ ਮਾਲਕ ਕੀ ਕਹਿੰਦੇ ਹਨ",
    previewNotice: "ਪੂਰਵਝਲਕ ਪਲੇਸਹੋਲਡਰ - ਉਤਪਾਦਨ ਵਿੱਚ ਨਹੀਂ ਦਿਖਦੇ",
    googleCta: "Google ਉੱਤੇ ਹੋਰ ਸਮੀਖਿਆਵਾਂ ਪੜ੍ਹੋ:",
    googleLinkLabel: "Google ਸਮੀਖਿਆਵਾਂ",
  },
  services: {
    eyebrow: "ਸੇਵਾਵਾਂ",
    title: "ਮੈਟਰੋ ਵੈਨਕੂਵਰ ਦੇ ਘਰਾਂ ਲਈ ਛੱਤ ਸੇਵਾਵਾਂ",
    body: "ਇੱਕ ਜ਼ਰੂਰੀ ਲੀਕ ਤੋਂ ਇੱਕ ਪੂਰੀ ਆਰਕੀਟੈਕਚਰਲ ਰੀ-ਰੂਫ ਤੱਕ, ਹਰ ਪ੍ਰੋਜੈਕਟ ਉਸੇ ਮੁਲਾਂਕਣ ਅਨੁਸ਼ਾਸਨ ਨਾਲ ਸ਼ੁਰੂ ਹੁੰਦਾ ਹੈ।",
    items: [
      {
        title: "ਛੱਤ ਬਦਲਣਾ",
        intent: "ਮੇਰੀ ਛੱਤ ਆਪਣੀ ਸੇਵਾ ਜੀਵਨ ਦੇ ਅੰਤ 'ਤੇ ਹੈ",
        body: "ਤੁਹਾਡੇ ਘਰ ਦੇ ਅਨੁਕੂਲ ਅਸੈਂਬਲੀ, ਅੰਡਰਲੇਮੈਂਟ, ਹਵਾਦਾਰੀ ਅਤੇ ਫਲੈਸ਼ਿੰਗ ਵੇਰਵਿਆਂ ਸਮੇਤ ਪੁਰਾਣੀ ਛੱਤ ਪੂਰੀ ਹਟਾ ਕੇ ਨਵੀਂ ਛੱਤ ਲਗਾਉਣਾ।",
      },
      {
        title: "ਛੱਤ ਦੀ ਮੁਰੰਮਤ ਅਤੇ ਲੀਕ ਨਿਦਾਨ",
        intent: "ਮੇਰੇ ਕੋਲ ਲੀਕ ਜਾਂ ਦਿਖਾਈ ਦੇਣ ਵਾਲਾ ਨੁਕਸਾਨ ਹੈ",
        body: "ਮੁਲਾਂਕਣ ਮੁਰੰਮਤ ਦੇ ਦਾਇਰੇ ਦੇ ਪ੍ਰਸਤਾਵਿਤ ਹੋਣ ਤੋਂ ਪਹਿਲਾਂ ਆਮ ਲੀਕ ਮਾਰਗਾਂ ਜਿਵੇਂ ਕਿ ਫਲੈਸ਼ਿੰਗ, ਪ੍ਰਵੇਸ਼ ਅਤੇ ਡਰੇਨੇਜ 'ਤੇ ਵਿਚਾਰ ਕਰਦਾ ਹੈ।",
      },
      {
        title: "ਆਰਕੀਟੈਕਚਰਲ ਅਸਫਾਲਟ ਸ਼ਿੰਗਲਜ਼",
        intent: "ਮੈਨੂੰ ਇੱਕ ਸਾਬਤ, ਮੁੱਖ ਧਾਰਾ ਵਾਲੀ ਛੱਤ ਚਾਹੀਦੀ ਹੈ",
        body: "ਢਲਾਣ ਵਾਲੀਆਂ ਛੱਤਾਂ ਲਈ ਇੱਕ ਮਜ਼ਬੂਤ ​​ਰਿਹਾਇਸ਼ੀ ਵਿਕਲਪ, ਪ੍ਰੋਫਾਈਲਾਂ ਅਤੇ ਰੰਗਾਂ ਦੀ ਇੱਕ ਵਿਸ਼ਾਲ ਸ਼੍ਰੇਣੀ ਦੇ ਨਾਲ।",
      },
      {
        title: "ਸਟੈਂਡਿੰਗ-ਸੀਮ ਮੈਟਲ ਰੂਫਿੰਗ",
        intent: "ਮੈਂ ਲੰਬੀ ਉਮਰ ਅਤੇ ਆਧੁਨਿਕ ਦਿੱਖ ਚਾਹੁੰਦਾ ਹਾਂ",
        body: "ਸਮਕਾਲੀ ਅਤੇ ਵੈਸਟ ਕੋਸਟ ਆਰਕੀਟੈਕਚਰ ਲਈ ਇੱਕ ਪ੍ਰੀਮੀਅਮ ਵਿਕਲਪ, ਛੁਪੇ ਹੋਏ ਫਾਸਟਨਰ ਅਤੇ ਕਰਿਸਪ ਲਾਈਨਾਂ ਦੇ ਨਾਲ।",
      },
      {
        title: "ਫਲੈਟ ਅਤੇ ਘੱਟ ਢਲਾਨ ਵਾਲੀ ਛੱਤ",
        intent: "ਮੇਰੀ ਛੱਤ ਸਮਤਲ ਜਾਂ ਘੱਟ ਢਲਾਨ ਵਾਲੀ ਹੈ",
        body: "ਪੈਰਾਪੈਟਸ, ਕਰਬ ਅਤੇ ਪਰਿਵਰਤਨ 'ਤੇ ਡਰੇਨੇਜ, ਢਲਾਨ ਅਤੇ ਵੇਰਵੇ ਲਈ ਦਰਸਾਏ ਗਏ ਝਿੱਲੀ ਪ੍ਰਣਾਲੀਆਂ।",
      },
      {
        title: "ਛੱਤ ਦਾ ਨਿਰੀਖਣ ਅਤੇ ਰੱਖ-ਰਖਾਅ",
        intent: "ਮੈਂ ਜਾਣਨਾ ਚਾਹੁੰਦਾ ਹਾਂ ਕਿ ਮੈਂ ਕਿੱਥੇ ਖੜ੍ਹਾ ਹਾਂ",
        body: "ਸੂਚਿਤ ਯੋਜਨਾਬੰਦੀ ਦਾ ਸਮਰਥਨ ਕਰਨ ਲਈ ਦਸਤਾਵੇਜ਼ੀ ਸਥਿਤੀ ਮੁਲਾਂਕਣ ਅਤੇ ਰੱਖ-ਰਖਾਅ ਦੀਆਂ ਤਰਜੀਹਾਂ।",
      },
    ],
  },
  comparison: {
    eyebrow: "ਛੱਤ ਸਿਸਟਮ",
    title: "ਤੁਹਾਡੇ ਘਰ ਲਈ ਕਿਹੜੀ ਛੱਤ ਸਹੀ ਹੈ?",
    body: "ਛੱਤ ਲਈ ਕੋਈ ਇੱਕ ਸਮੱਗਰੀ ਹਰ ਘਰ ਵਾਸਤੇ ਸਭ ਤੋਂ ਵਧੀਆ ਨਹੀਂ ਹੁੰਦੀ। ਸਹੀ ਅਸੈਂਬਲੀ ਢਲਾਨ, ਆਰਕੀਟੈਕਚਰ, ਮੌਸਮੀ ਪ੍ਰਭਾਵ, ਡਰੇਨੇਜ, ਬਜਟ ਅਤੇ ਲਾਗੂ ਸਥਾਨਕ ਲੋੜਾਂ ਉੱਤੇ ਨਿਰਭਰ ਕਰਦੀ ਹੈ। ਮੁੱਖ ਵਿਕਲਪਾਂ ਦੀ ਆਮ ਤੁਲਨਾ ਹੇਠਾਂ ਦਿੱਤੀ ਹੈ।",
    columns: {
      application: "ਆਦਰਸ਼ ਐਪਲੀਕੇਸ਼ਨ",
      character: "ਡਿਜ਼ਾਈਨ ਦੀ ਦਿੱਖ",
      maintenance: "ਰੱਖ-ਰਖਾਅ ਦੇ ਵਿਚਾਰ",
      investment: "ਤੁਲਨਾਤਮਕ ਨਿਵੇਸ਼",
      performance: "ਪ੍ਰਦਰਸ਼ਨ ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ",
    },
    systems: [
      {
        name: "ਆਰਕੀਟੈਕਚਰਲ ਅਸਫਾਲਟ ਸ਼ਿੰਗਲਜ਼",
        summary: "ਇੱਕ ਮਜ਼ਬੂਤ ​​ਮੁੱਖ ਧਾਰਾ ਰਿਹਾਇਸ਼ੀ ਵਿਕਲਪ।",
        application: "ਜ਼ਿਆਦਾਤਰ ਲੋਅਰ ਮੇਨਲੈਂਡ ਘਰਾਂ 'ਤੇ ਢਲਾਣ ਵਾਲੀਆਂ ਛੱਤਾਂ।",
        character: "ਅਯਾਮੀ ਟੈਕਸਟ, ਪਰੰਪਰਾਗਤ ਤੋਂ ਪਰਿਵਰਤਨਸ਼ੀਲ।",
        maintenance: "ਸਮੇਂ-ਸਮੇਂ ਤੇ ਨਿਰੀਖਣ; ਤੱਟਵਰਤੀ ਮੌਸਮ ਵਿੱਚ ਮੌਸ ਕੰਟਰੋਲ ਮਾਅਨੇ ਰੱਖਦਾ ਹੈ।",
        investment: "ਮੱਧਮ",
        performance:
          "ਵਿਆਪਕ ਉਤਪਾਦ ਉਪਲਬਧਤਾ ਅਤੇ ਸਿੱਧੇ ਭਵਿੱਖ ਦੀ ਮੁਰੰਮਤ ਦੇ ਨਾਲ ਚੰਗੀ ਤਰ੍ਹਾਂ ਸਮਝਿਆ ਗਿਆ ਸਿਸਟਮ।",
      },
      {
        name: "ਖੜੀ-ਸੀਮ ਧਾਤੂ",
        summary: "ਇੱਕ ਪ੍ਰੀਮੀਅਮ ਲੰਬੀ ਉਮਰ ਅਤੇ ਡਿਜ਼ਾਈਨ ਵਿਕਲਪ।",
        application: "ਸਾਫ਼ ਛੱਤ ਸਤ੍ਹਾਂ ਵਾਲੀ ਸਮਕਾਲੀ ਅਤੇ ਵੈਸਟ ਕੋਸਟ ਆਰਕੀਟੈਕਚਰ।",
        character: "ਕਰਿਸਪ, ਰੇਖਿਕ, ਆਰਕੀਟੈਕਚਰਲ.",
        maintenance: "ਘੱਟ ਰੁਟੀਨ ਰੱਖ-ਰਖਾਅ; ਵੇਰਵੇ ਅਤੇ ਬੰਨ੍ਹਣ ਦੀ ਗੁਣਵੱਤਾ ਮਹੱਤਵਪੂਰਨ ਹੈ।",
        investment: "ਉੱਚਾ",
        performance:
          "ਛੁਪੇ ਹੋਏ ਫਾਸਟਨਰ ਅਤੇ ਨਿਰੰਤਰ ਪੈਨਲ ਖੁੱਲ੍ਹੀਆਂ ਉਚਾਈਆਂ 'ਤੇ ਪਾਣੀ ਨੂੰ ਚੰਗੀ ਤਰ੍ਹਾਂ ਵਹਾਉਂਦੇ ਹਨ।",
      },
      {
        name: "ਫਲੈਟ ਅਤੇ ਘੱਟ-ਢਲਾਨ ਝਿੱਲੀ",
        summary: "ਘੱਟ ਢਲਾਨ ਵਾਲੇ ਖੇਤਰਾਂ ਲਈ ਸਿਸਟਮਾਂ ਦਾ ਢੁਕਵਾਂ ਪਰਿਵਾਰ।",
        application: "ਫਲੈਟ ਅਤੇ ਘੱਟ-ਢਲਾਨ ਵਾਲੀ ਛੱਤ ਵਾਲੇ ਖੇਤਰ, ਡੇਕ ਅਤੇ ਜੋੜ।",
        character: "ਅਰਾਮਦੇਹ - ਛੱਤ ਇੱਕ ਵਿਜ਼ੂਅਲ ਵਿਸ਼ੇਸ਼ਤਾ ਨਹੀਂ ਹੈ.",
        maintenance: "ਡਰੇਨ ਅਤੇ ਸੀਮ ਦਾ ਨਿਰੀਖਣ; ਡਰੇਨੇਜ ਦੇ ਰਸਤੇ ਸਾਫ਼ ਰੱਖੋ।",
        investment: "ਝਿੱਲੀ ਅਤੇ ਅਸੈਂਬਲੀ ਦੁਆਰਾ ਬਦਲਦਾ ਹੈ",
        performance:
          "ਪ੍ਰਦਰਸ਼ਨ ਢਲਾਨ ਤੋਂ ਡਰੇਨ, ਝਿੱਲੀ ਦੀ ਚੋਣ ਅਤੇ ਤਬਦੀਲੀ ਦੇ ਵੇਰਵੇ 'ਤੇ ਬਹੁਤ ਜ਼ਿਆਦਾ ਨਿਰਭਰ ਕਰਦਾ ਹੈ।",
      },
    ],
    disclaimer:
      "ਕੇਵਲ ਮਾਰਗਦਰਸ਼ਨ. ਸਮੱਗਰੀ ਦੀ ਚੋਣ ਅਤੇ ਅਸੈਂਬਲੀ ਦਾ ਵੇਰਵਾ ਮੌਜੂਦਾ RCABC ਮਾਰਗਦਰਸ਼ਨ ਅਤੇ ਲਾਗੂ ਬੀ ਸੀ ਅਤੇ ਮਿਉਂਸਪਲ ਲੋੜਾਂ ਦੀ ਪਾਲਣਾ ਕਰਦਾ ਹੈ। ਅਸੀਂ ਇੰਜੀਨੀਅਰਿੰਗ ਗਾਰੰਟੀ ਪ੍ਰਦਾਨ ਨਹੀਂ ਕਰਦੇ ਹਾਂ।",
  },
  why: {
    eyebrow: "ਕਿਉਂ Revolin ਛੱਤ",
    title: "ਇੱਕ ਉਸਾਰੀ ਕੰਪਨੀ ਦੀ ਛੱਤ ਵੰਡ",
    body: "ਰੇਵੋਲਿਨ ਰੂਫਿੰਗ ਰਿਹਾਇਸ਼ੀ ਛੱਤਾਂ ਲਈ ਰੇਵੋਲਿਨ ਗਰੁੱਪ ਦੀ ਪ੍ਰੋਜੈਕਟ ਪਹੁੰਚ ਨੂੰ ਲਾਗੂ ਕਰਦੀ ਹੈ: ਪਹਿਲਾਂ ਮੁਲਾਂਕਣ, ਤੱਟਵਰਤੀ-ਮੌਸਮ ਦੇ ਵੇਰਵਿਆਂ ਵੱਲ ਇੱਕ ਲਿਖਤੀ ਪ੍ਰਸਤਾਵਿਤ ਸਕੋਪ ਅਤੇ ਧਿਆਨ।",
    items: [
      {
        title: "ਸਿਫਾਰਸ਼ ਤੋਂ ਪਹਿਲਾਂ ਮੁਲਾਂਕਣ",
        body: "ਅਸੀਂ ਇੱਕ ਦਾਇਰੇ ਦਾ ਪ੍ਰਸਤਾਵ ਕਰਨ ਤੋਂ ਪਹਿਲਾਂ ਜੋ ਅਸੀਂ ਲੱਭਦੇ ਹਾਂ ਉਸ ਦਾ ਦਸਤਾਵੇਜ਼ੀਕਰਨ ਕਰਦੇ ਹਾਂ।",
      },
      {
        title: "ਪਾਰਦਰਸ਼ੀ ਸਕੋਪ ਅਤੇ ਕੀਮਤ",
        body: "ਪ੍ਰਸਤਾਵਿਤ ਦਾਇਰੇ ਵਿੱਚ ਸਮਾਵੇਸ਼, ਬੇਦਖਲੀ ਅਤੇ ਸ਼ਰਤਾਂ ਦੀ ਪਛਾਣ ਕੀਤੀ ਜਾਂਦੀ ਹੈ ਜਿਨ੍ਹਾਂ ਵਿੱਚ ਤਬਦੀਲੀ ਦੀ ਲੋੜ ਹੋ ਸਕਦੀ ਹੈ।",
      },
      {
        title: "ਆਰਕੀਟੈਕਚਰਲ ਧਿਆਨ",
        body: "ਇਮਾਰਤ ਦੇ ਅਨੁਕੂਲ ਹੋਣ ਲਈ ਛੱਤ ਦੀਆਂ ਲਾਈਨਾਂ, ਰੰਗ ਅਤੇ ਪ੍ਰੋਫਾਈਲ ਚੁਣੇ ਗਏ ਹਨ।",
      },
      {
        title: "ਫਲੈਸ਼ਿੰਗ ਅਤੇ ਪਰਿਵਰਤਨ ਵੇਰਵੇ",
        body: "ਪਰਿਵਰਤਨ ਆਮ ਜਾਂਚ ਪੁਆਇੰਟ ਹੁੰਦੇ ਹਨ, ਇਸਲਈ ਉਹਨਾਂ ਨੂੰ ਮੁਲਾਂਕਣ ਅਤੇ ਸਕੋਪਿੰਗ ਦੌਰਾਨ ਜਾਣਬੁੱਝ ਕੇ ਧਿਆਨ ਦਿੱਤਾ ਜਾਂਦਾ ਹੈ।",
      },
      {
        title: "ਸਾਈਟ ਦੀ ਯੋਜਨਾਬੰਦੀ",
        body: "ਵਿਸ਼ੇਸ਼ ਸੰਪਤੀ ਲਈ ਪਹੁੰਚ, ਸੁਰੱਖਿਆ ਅਤੇ ਸਫਾਈ ਦੀਆਂ ਉਮੀਦਾਂ ਨੂੰ ਸੰਬੋਧਿਤ ਕੀਤਾ ਗਿਆ ਹੈ।",
      },
      {
        title: "ਪ੍ਰੋਜੈਕਟ ਸੰਚਾਰ",
        body: "ਪ੍ਰੋਜੈਕਟ ਦੇ ਦਾਇਰੇ ਨਾਲ ਅਨੁਸੂਚੀ ਅਤੇ ਸੰਚਾਰ ਉਮੀਦਾਂ ਦੀ ਪੁਸ਼ਟੀ ਕੀਤੀ ਜਾਂਦੀ ਹੈ।",
      },
      {
        title: "ਗੁਣਵੱਤਾ-ਨਿਯੰਤਰਣ ਚੌਕੀਆਂ",
        body: "ਇੰਸਟਾਲੇਸ਼ਨ ਦੇ ਦੌਰਾਨ ਅਤੇ ਅੰਤ ਵਿੱਚ ਪਰਿਭਾਸ਼ਿਤ ਜਾਂਚਾਂ।",
      },
      {
        title: "ਮੂਲ ਕੰਪਨੀ ਦੀ ਮੁਹਾਰਤ",
        body: "ਰੇਵੋਲਿਨ ਗਰੁੱਪ ਦੀ ਵਿਸ਼ਾਲ ਨਿਰਮਾਣ ਸਮਰੱਥਾ ਤੱਕ ਪਹੁੰਚ ਜਦੋਂ ਛੱਤ ਦਾ ਮੁੱਦਾ ਸਿਰਫ ਛੱਤ ਦਾ ਮੁੱਦਾ ਨਹੀਂ ਹੁੰਦਾ।",
      },
    ],
  },
  beforeAfter: {
    eyebrow: "ਪਹਿਲਾਂ ਅਤੇ ਬਾਅਦ",
    title: "ਵੇਰਵੇ ਵਿੱਚ ਅੰਤਰ ਵੇਖੋ",
    body: "ਤੁਲਨਾ ਕਰਨ ਲਈ ਹੈਂਡਲ ਘਸੀਟੋ: ਮੈਟਰੋ ਵੈਨਕੂਵਰ ਘਰਾਂ ਲਈ ਛੱਤ ਦੀਆਂ ਪਹਿਲਾਂ-ਬਾਅਦ ਤਬਦੀਲੀਆਂ ਵੇਖੋ।",
    beforeLabel: "ਪਹਿਲਾਂ",
    afterLabel: "ਤੋਂ ਬਾਅਦ",
    sliderLabel: "ਤੁਲਨਾ ਸਲਾਈਡਰ ਤੋਂ ਪਹਿਲਾਂ ਅਤੇ ਬਾਅਦ ਵਿੱਚ",
  },
  process: {
    eyebrow: "ਪ੍ਰਕਿਰਿਆ",
    title: "ਰੇਵੋਲਿਨ ਰੂਫਿੰਗ ਪ੍ਰੋਜੈਕਟ ਕਿਵੇਂ ਚੱਲਦਾ ਹੈ",
    steps: [
      {
        n: "01",
        title: "ਛੱਤ ਦਾ ਮੁਲਾਂਕਣ",
        body: "ਅਸੀਂ ਛੱਤ, ਫਲੈਸ਼ਿੰਗਜ਼, ਡਰੇਨੇਜ ਅਤੇ ਅੰਦਰੂਨੀ ਸਬੂਤਾਂ ਦਾ ਮੁਆਇਨਾ ਕਰਦੇ ਹਾਂ, ਅਤੇ ਸਥਿਤੀ ਦਾ ਦਸਤਾਵੇਜ਼ੀਕਰਨ ਕਰਦੇ ਹਾਂ।",
      },
      {
        n: "02",
        title: "ਸਕੋਪ ਅਤੇ ਛੱਤ ਦੀ ਸਿਫ਼ਾਰਿਸ਼",
        body: "ਪ੍ਰਸਤਾਵ ਸਮੀਖਿਆ ਲਈ ਢੁਕਵੇਂ ਵਿਕਲਪਾਂ, ਤਰਕ, ਦਾਇਰੇ ਅਤੇ ਕੀਮਤ ਦਾ ਦਸਤਾਵੇਜ਼ ਬਣਾ ਸਕਦਾ ਹੈ।",
      },
      {
        n: "03",
        title: "ਇੰਸਟਾਲੇਸ਼ਨ / ਮੁਰੰਮਤ",
        body: "ਦਸਤਾਵੇਜ਼ੀ ਦਾਇਰੇ, ਸਾਈਟ ਪਲਾਨ ਅਤੇ ਲਾਗੂ ਪ੍ਰੋਜੈਕਟ ਲੋੜਾਂ ਲਈ ਪ੍ਰਵਾਨਿਤ ਕੰਮ ਦੀ ਕਾਰਵਾਈ।",
      },
      {
        n: "04",
        title: "ਅੰਤਿਮ ਨਿਰੀਖਣ ਅਤੇ ਸਪੁਰਦਗੀ",
        body: "ਸੰਪੂਰਨਤਾ ਦੀਆਂ ਜ਼ਰੂਰਤਾਂ ਅਤੇ ਕੋਈ ਵੀ ਪ੍ਰੋਜੈਕਟ ਦਸਤਾਵੇਜ਼ ਸਹਿਮਤ ਹੋਏ ਦਾਇਰੇ ਦੀ ਪਾਲਣਾ ਕਰਦੇ ਹਨ।",
      },
    ],
  },
  weather: {
    eyebrow: "ਬੀ ਸੀ ਦੀਆਂ ਸਥਿਤੀਆਂ",
    title: "ਇੱਥੇ ਇੱਕ ਛੱਤ ਇੱਕ ਪਾਣੀ-ਪ੍ਰਬੰਧਨ ਪ੍ਰਣਾਲੀ ਹੈ",
    body: "ਲੋਅਰ ਮੇਨਲੈਂਡ ਵਿੱਚ ਇੱਕ ਛੱਤ ਸੂਰਜ ਦੀ ਬਜਾਏ ਲੰਮੀ ਬਾਰਿਸ਼ ਦੇ ਪ੍ਰਬੰਧਨ ਵਿੱਚ ਸਾਲ ਦਾ ਬਹੁਤ ਸਾਰਾ ਸਮਾਂ ਬਿਤਾਉਂਦੀ ਹੈ। ਇਹ ਬਦਲਦਾ ਹੈ ਜੋ ਮਾਇਨੇ ਰੱਖਦਾ ਹੈ.",
    items: [
      {
        title: "ਲੰਮੀ ਬਾਰਿਸ਼ ਅਤੇ ਨਮੀ",
        body: "ਇੱਕ ਗਿੱਲੇ ਤੱਟਵਰਤੀ ਮਾਹੌਲ ਵਿੱਚ ਅੰਡਰਲੇਮੈਂਟ, ਲੈਪਸ ਅਤੇ ਹਵਾਦਾਰੀ ਜ਼ਿਆਦਾ ਭਾਰ ਪਾਉਂਦੀ ਹੈ।",
      },
      {
        title: "ਹਵਾ ਅਤੇ ਐਕਸਪੋਜਰ",
        body: "ਐਕਸਪੋਜ਼ਡ ਐਲੀਵੇਸ਼ਨਜ਼ ਅਤੇ ਖੁੱਲੀਆਂ ਸਾਈਟਾਂ ਫਾਸਟਨਿੰਗ ਅਤੇ ਕਿਨਾਰੇ ਦੇ ਵੇਰਵੇ ਨੂੰ ਪ੍ਰਭਾਵਤ ਕਰਦੀਆਂ ਹਨ।",
      },
      {
        title: "ਡਰੇਨੇਜ",
        body: "ਢਲਾਣ ਤੋਂ ਡਰੇਨ, ਗਟਰ ਅਤੇ ਡਾਊਨਸਪਾਉਟ ਸਮਰੱਥਾ ਇਹ ਫੈਸਲਾ ਕਰਦੀ ਹੈ ਕਿ ਪਾਣੀ ਲੋਡ ਹੇਠ ਕਿੱਥੇ ਜਾਂਦਾ ਹੈ।",
      },
      {
        title: "ਬਰਫ਼ ਜਿੱਥੇ ਲਾਗੂ ਹੋਵੇ",
        body: "ਖੇਤਰ ਵਿੱਚ ਉੱਚੀਆਂ ਉਚਾਈਆਂ ਬਰਫ਼ ਦੇ ਭਾਰ ਅਤੇ ਪਿਘਲਦੇ ਪਾਣੀ ਦੇ ਚੱਕਰ ਨੂੰ ਵੇਖਦੀਆਂ ਹਨ ਜੋ ਚਾਪਲੂਸ ਸਾਈਟਾਂ ਨਹੀਂ ਕਰਦੀਆਂ।",
      },
      {
        title: "ਛੱਤ ਦੀ ਜਿਓਮੈਟਰੀ",
        body: "ਵੈਲੀਜ਼, ਡੋਰਮਰਸ, ਸਕਾਈਲਾਈਟਸ ਅਤੇ ਐਡੀਸ਼ਨਸ ਤਬਦੀਲੀਆਂ ਬਣਾਉਂਦੇ ਹਨ ਜਿਨ੍ਹਾਂ ਨੂੰ ਸਭ ਤੋਂ ਵੱਧ ਦੇਖਭਾਲ ਦੀ ਲੋੜ ਹੁੰਦੀ ਹੈ।",
      },
    ],
  },
  areas: {
    eyebrow: "ਸੇਵਾ ਖੇਤਰ",
    title: "ਮੈਟਰੋ ਵੈਨਕੂਵਰ ਅਤੇ ਲੋਅਰ ਮੇਨਲੈਂਡ ਦੀ ਸੇਵਾ",
    body: "ਜੇਕਰ ਤੁਹਾਡਾ ਭਾਈਚਾਰਾ ਨੇੜੇ ਹੈ ਪਰ ਸੂਚੀਬੱਧ ਨਹੀਂ ਹੈ, ਤਾਂ ਸਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰੋ ਅਤੇ ਅਸੀਂ ਉਪਲਬਧਤਾ ਦੀ ਪੁਸ਼ਟੀ ਕਰਾਂਗੇ।",
  },
  faq: {
    eyebrow: "FAQ",
    title: "ਘਰ ਦੇ ਮਾਲਕ ਦੇ ਸਵਾਲ",
    items: [
      {
        q: "ਕੀ ਮੈਨੂੰ ਛੱਤ ਦੀ ਮੁਰੰਮਤ ਜਾਂ ਪੂਰੀ ਤਬਦੀਲੀ ਦੀ ਲੋੜ ਹੈ?",
        a: "ਇਹ ਅਸੈਂਬਲੀ ਦੀ ਉਮਰ ਅਤੇ ਸਥਿਤੀ 'ਤੇ ਨਿਰਭਰ ਕਰਦਾ ਹੈ ਅਤੇ ਕੀ ਸਮੱਸਿਆ ਨੂੰ ਅਲੱਗ ਕੀਤਾ ਗਿਆ ਹੈ. ਇੱਕ ਸਥਾਨਕ ਮੁੱਦਾ ਇੱਕ ਮੁਰੰਮਤ ਦੇ ਦਾਇਰੇ ਦਾ ਸਮਰਥਨ ਕਰ ਸਕਦਾ ਹੈ, ਜਦੋਂ ਕਿ ਇੱਕ ਪੁਰਾਣੀ ਕਵਰਿੰਗ ਜਾਂ ਮਲਟੀਪਲ ਅਸਫਲਤਾ ਬਿੰਦੂ ਬਦਲਣ 'ਤੇ ਵਿਚਾਰ ਕਰਨ ਨੂੰ ਜਾਇਜ਼ ਠਹਿਰਾ ਸਕਦੇ ਹਨ। ਇੱਕ ਮੁਲਾਂਕਣ ਦਸਤਾਵੇਜ਼ ਵਿੱਚ ਮਦਦ ਕਰਦਾ ਹੈ ਕਿ ਕਿਹੜੀਆਂ ਚੋਣਾਂ ਦੇਖੀਆਂ ਗਈਆਂ ਹਾਲਤਾਂ ਵਿੱਚ ਫਿੱਟ ਹੁੰਦੀਆਂ ਹਨ।",
      },
      {
        q: "ਮੇਰੀ ਛੱਤ ਲੀਕ ਹੋ ਰਹੀ ਹੈ। ਮੈਨੂੰ ਪਹਿਲਾਂ ਕੀ ਕਰਨਾ ਚਾਹੀਦਾ ਹੈ?",
        a: "ਅੰਦਰ ਪਾਣੀ ਰੱਖੋ, ਫੋਟੋ ਖਿੱਚੋ ਜੋ ਤੁਸੀਂ ਸੁਰੱਖਿਅਤ ਰੂਪ ਨਾਲ ਦੇਖ ਸਕਦੇ ਹੋ, ਅਤੇ ਸਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰੋ। ਗਿੱਲੀ ਛੱਤ 'ਤੇ ਨਾ ਜਾਓ। ਲੀਕ ਅਕਸਰ ਦਿਖਾਈ ਦੇਣ ਵਾਲੇ ਧੱਬੇ ਤੋਂ ਦੂਰ ਹੁੰਦੇ ਹਨ, ਇਸਲਈ ਨਿਦਾਨ ਪੈਚ ਨਾਲੋਂ ਜ਼ਿਆਦਾ ਮਾਇਨੇ ਰੱਖਦਾ ਹੈ।",
      },
      {
        q: "ਛੱਤ ਦੇ ਮੁਲਾਂਕਣ ਦੌਰਾਨ ਕੀ ਹੁੰਦਾ ਹੈ?",
        a: "ਅਸੀਂ ਛੱਤ ਦੇ ਢੱਕਣ, ਫਲੈਸ਼ਿੰਗ, ਪ੍ਰਵੇਸ਼, ਹਵਾਦਾਰੀ ਅਤੇ ਡਰੇਨੇਜ ਦੀ ਸਮੀਖਿਆ ਕਰਦੇ ਹਾਂ, ਅੰਦਰੂਨੀ ਸਬੂਤ ਲੱਭਦੇ ਹਾਂ ਜਿੱਥੇ ਢੁਕਵਾਂ ਹੋਵੇ, ਅਤੇ ਦਸਤਾਵੇਜ਼ ਖੋਜਾਂ। ਫਿਰ ਤੁਹਾਨੂੰ ਇੱਕ ਲਿਖਤੀ ਸਕੋਪ ਅਤੇ ਸਿਫਾਰਸ਼ ਪ੍ਰਾਪਤ ਹੁੰਦੀ ਹੈ।",
      },
      {
        q: "ਕਿਹੜੀ ਛੱਤ ਵਾਲੀ ਸਮੱਗਰੀ ਸਭ ਤੋਂ ਵਧੀਆ ਹੈ?",
        a: "ਹਰ ਘਰ ਲਈ ਕੋਈ ਵੀ ਸਮੱਗਰੀ ਵਧੀਆ ਨਹੀਂ ਹੈ। ਢਲਾਨ, ਆਰਕੀਟੈਕਚਰ, ਐਕਸਪੋਜ਼ਰ, ਡਰੇਨੇਜ, ਬਜਟ ਅਤੇ ਲਾਗੂ ਸਥਾਨਕ ਲੋੜਾਂ ਸਭ ਕਾਰਕ ਵਿੱਚ ਹਨ। ਅਸੀਂ ਇੱਕ ਉਤਪਾਦ ਨੂੰ ਅੱਗੇ ਵਧਾਉਣ ਦੀ ਬਜਾਏ, ਵਪਾਰ-ਆਫ ਦੀ ਵਿਆਖਿਆ ਕਰਦੇ ਹਾਂ ਅਤੇ ਅਸੈਂਬਲੀ ਦੀ ਸਿਫਾਰਸ਼ ਕਰਦੇ ਹਾਂ।",
      },
      {
        q: "ਕੀ ਤੁਸੀਂ ਫਲੈਟ ਅਤੇ ਘੱਟ ਢਲਾਣ ਵਾਲੀਆਂ ਛੱਤਾਂ 'ਤੇ ਕੰਮ ਕਰ ਸਕਦੇ ਹੋ?",
        a: "ਹਾਂ। ਘੱਟ-ਢਲਾਨ ਵਾਲੇ ਖੇਤਰਾਂ ਨੂੰ ਝਿੱਲੀ ਪ੍ਰਣਾਲੀਆਂ ਵਜੋਂ ਦਰਸਾਏ ਗਏ ਹਨ, ਖਾਸ ਤੌਰ 'ਤੇ ਢਲਾਨ-ਤੋਂ-ਨਿਕਾਸ ਅਤੇ ਪੈਰਾਪੈਟਸ, ਕਰਬਸ ਅਤੇ ਪਰਿਵਰਤਨ 'ਤੇ ਵੇਰਵੇ ਦੇ ਨਾਲ।",
      },
      {
        q: "ਧਾਤ ਜਾਂ ਸ਼ਿੰਗਲਜ਼?",
        a: "ਆਰਕੀਟੈਕਚਰਲ ਅਸਫਾਲਟ ਸ਼ਿੰਗਲਜ਼ ਇੱਕ ਮਜ਼ਬੂਤ ​​ਮੁੱਖ ਧਾਰਾ ਰਿਹਾਇਸ਼ੀ ਵਿਕਲਪ ਹਨ। ਸਟੈਂਡਿੰਗ-ਸੀਮ ਮੈਟਲ ਇੱਕ ਪ੍ਰੀਮੀਅਮ ਲੰਬੀ ਉਮਰ ਅਤੇ ਡਿਜ਼ਾਈਨ ਵਿਕਲਪ ਹੈ ਜੋ ਸਮਕਾਲੀ ਅਤੇ ਵੈਸਟ ਕੋਸਟ ਆਰਕੀਟੈਕਚਰ ਦੇ ਅਨੁਕੂਲ ਹੈ। ਦੋਵੇਂ ਸਹੀ ਹੋ ਸਕਦੇ ਹਨ - ਇਹ ਇਮਾਰਤ ਅਤੇ ਤੁਹਾਡੀਆਂ ਤਰਜੀਹਾਂ 'ਤੇ ਨਿਰਭਰ ਕਰਦਾ ਹੈ।",
      },
      {
        q: "ਛੱਤ ਦੇ ਪ੍ਰੋਜੈਕਟ ਨੂੰ ਕਿੰਨਾ ਸਮਾਂ ਲੱਗਦਾ ਹੈ?",
        a: "ਸਮਾਂਰੇਖਾ ਆਕਾਰ, ਗੁੰਝਲਤਾ, ਪਹੁੰਚ ਅਤੇ ਚੁਣੇ ਗਏ ਸਿਸਟਮ 'ਤੇ ਨਿਰਭਰ ਕਰਦੀ ਹੈ। ਅਸੀਂ ਤੁਹਾਡੇ ਦਾਇਰੇ ਦੇ ਨਾਲ ਉਮੀਦ ਕੀਤੀ ਮਿਆਦ ਪ੍ਰਦਾਨ ਕਰਦੇ ਹਾਂ, ਅਤੇ ਜੇਕਰ ਹਾਲਾਤ ਬਦਲਦੇ ਹਨ ਤਾਂ ਤੁਹਾਨੂੰ ਅੱਪਡੇਟ ਕਰਦੇ ਹਾਂ।",
      },
      {
        q: "ਮੈਂ ਇੱਕ ਹਵਾਲਾ ਕਿਵੇਂ ਪ੍ਰਾਪਤ ਕਰਾਂ?",
        a: "ਛੱਤ ਦੇ ਮੁਲਾਂਕਣ ਲਈ ਬੇਨਤੀ ਕਰੋ। ਅਸੀਂ ਛੱਤ ਨੂੰ ਦੇਖਾਂਗੇ, ਫਿਰ ਇੱਕ ਲਿਖਤੀ ਸਕੋਪ ਅਤੇ ਕੀਮਤ ਪ੍ਰਦਾਨ ਕਰਾਂਗੇ। ਅਸੀਂ ਛੱਤ ਦੇਖੇ ਬਿਨਾਂ ਮੁੜ-ਛੱਤ ਦਾ ਹਵਾਲਾ ਨਹੀਂ ਦਿੰਦੇ।",
      },
      {
        q: "ਜੇਕਰ ਮੇਰੇ ਪ੍ਰੋਜੈਕਟ ਦੌਰਾਨ ਮੀਂਹ ਪੈਂਦਾ ਹੈ ਤਾਂ ਕੀ ਹੁੰਦਾ ਹੈ?",
        a: "ਮੌਸਮ ਸੁਰੱਖਿਅਤ ਕੰਮ, ਕ੍ਰਮ ਅਤੇ ਸਮਾਂ-ਸਾਰਣੀ ਨੂੰ ਪ੍ਰਭਾਵਿਤ ਕਰ ਸਕਦਾ ਹੈ। ਸੁਰੱਖਿਆ ਪਹੁੰਚ ਅਤੇ ਕਿਸੇ ਵੀ ਮੌਸਮ-ਸਬੰਧਤ ਤਬਦੀਲੀਆਂ ਦਾ ਪ੍ਰਬੰਧਨ ਪ੍ਰੋਜੈਕਟ-ਵਿਸ਼ੇਸ਼ ਯੋਜਨਾ ਅਤੇ ਸੰਚਾਰ ਪ੍ਰਕਿਰਿਆ ਦੁਆਰਾ ਕੀਤਾ ਜਾਂਦਾ ਹੈ।",
      },
      {
        q: "ਕਿਹੜੀ ਵਾਰੰਟੀ ਲਾਗੂ ਹੁੰਦੀ ਹੈ?",
        a: "ਉਤਪਾਦਕ ਕਵਰੇਜ ਉਤਪਾਦ ਦੁਆਰਾ ਵੱਖ-ਵੱਖ ਹੁੰਦੀ ਹੈ ਅਤੇ ਕਾਰੀਗਰੀ ਦੀਆਂ ਸ਼ਰਤਾਂ ਤੁਹਾਡੇ ਲਿਖਤੀ ਦਾਇਰੇ ਵਿੱਚ ਪੁਸ਼ਟੀ ਕੀਤੀਆਂ ਜਾਂਦੀਆਂ ਹਨ। ਅਸੀਂ ਇੱਥੇ ਵਾਰੰਟੀ ਦੀਆਂ ਸ਼ਰਤਾਂ ਨੂੰ ਪ੍ਰਕਾਸ਼ਿਤ ਨਹੀਂ ਕਰਦੇ ਹਾਂ: ਤੁਹਾਡੇ ਪ੍ਰੋਜੈਕਟ 'ਤੇ ਲਾਗੂ ਹੋਣ ਵਾਲੀਆਂ ਸ਼ਰਤਾਂ ਤੁਹਾਡੇ ਦਸਤਾਵੇਜ਼ਾਂ ਵਿੱਚ ਦੱਸੀਆਂ ਗਈਆਂ ਹਨ।",
      },
      {
        q: "ਤੁਸੀਂ ਕਿਹੜੇ ਖੇਤਰਾਂ ਵਿੱਚ ਸੇਵਾ ਕਰਦੇ ਹੋ?",
        a: "ਮੈਟਰੋ ਵੈਨਕੂਵਰ ਅਤੇ ਲੋਅਰ ਮੇਨਲੈਂਡ, ਇਸ ਪੰਨੇ 'ਤੇ ਸੂਚੀਬੱਧ ਭਾਈਚਾਰਿਆਂ ਸਮੇਤ। ਨੇੜੇ ਦੀ ਉਪਲਬਧਤਾ ਦੀ ਪੁਸ਼ਟੀ ਕਰਨ ਲਈ ਸਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰੋ।",
      },
    ],
  },
  form: {
    eyebrow: "ਮੁਲਾਂਕਣ ਲਈ ਬੇਨਤੀ ਕਰੋ",
    title: "ਛੱਤ ਦੀ ਜਾਂਚ ਬੁੱਕ ਕਰੋ",
    body: "ਸਾਨੂੰ ਜਾਇਦਾਦ, ਛੱਤ ਅਤੇ ਤੁਸੀਂ ਕੀ ਦੇਖਿਆ ਹੈ ਬਾਰੇ ਦੱਸੋ।",
    name: "ਪੂਰਾ ਨਾਂਮ",
    phone: "ਫ਼ੋਨ",
    email: "ਈਮੇਲ",
    postal: "ਡਾਕ ਕੋਡ",
    need: "ਛੱਤ ਦੀ ਲੋੜ",
    needOptions: [
      "ਛੱਤ ਦੀ ਤਬਦੀਲੀ",
      "ਛੱਤ ਦੀ ਮੁਰੰਮਤ / ਲੀਕ",
      "ਫਲੈਟ ਜਾਂ ਘੱਟ ਢਲਾਣ ਵਾਲੀ ਛੱਤ",
      "ਛੱਤ ਦਾ ਨਿਰੀਖਣ",
      "ਅਜੇ ਪੱਕਾ ਨਹੀਂ",
    ],
    timeline: "ਤਰਜੀਹੀ ਸਮਾਂਰੇਖਾ",
    timelineOptions: ["ਜਿੰਨੀ ਜਲਦੀ ਹੋ ਸਕੇ", "1-3 ਮਹੀਨਿਆਂ ਦੇ ਅੰਦਰ", "3-6 ਮਹੀਨੇ", "ਬਸ ਯੋਜਨਾਬੰਦੀ"],
    message: "ਸਮੱਸਿਆ ਦਾ ਵਰਣਨ ਕਰੋ (ਵਿਕਲਪਿਕ)",
    submit: "ਛੱਤ ਦੀ ਜਾਂਚ ਬੁੱਕ ਕਰੋ",
    submitting: "ਭੇਜਿਆ ਜਾ ਰਿਹਾ ਹੈ...",
    successTitle: "ਬੇਨਤੀ ਪ੍ਰਾਪਤ ਹੋਈ",
    successBody: "ਤੁਹਾਡਾ ਧੰਨਵਾਦ. ਤੁਹਾਡੀ ਛੱਤ ਦੇ ਮੁਲਾਂਕਣ ਦੀ ਬੇਨਤੀ ਸਫਲਤਾਪੂਰਵਕ ਪ੍ਰਦਾਨ ਕੀਤੀ ਗਈ ਸੀ।",
    errorRequired: "ਇਸ ਫੀਲਡ ਦੀ ਲੋੜ ਹੈ",
    errorEmail: "ਇੱਕ ਵੈਧ ਈਮੇਲ ਪਤਾ ਦਰਜ ਕਰੋ",
    errorPhone: "ਇੱਕ ਵੈਧ ਫ਼ੋਨ ਨੰਬਰ ਦਾਖਲ ਕਰੋ",
    errorSubmit: "ਤੁਹਾਡੀ ਬੇਨਤੀ ਨਹੀਂ ਭੇਜੀ ਜਾ ਸਕੀ। ਇਸ ਪੰਨੇ ਉੱਤੇ ਦਿੱਤੀ ਸੰਪਰਕ ਜਾਣਕਾਰੀ ਵਰਤੋ।",
    errorUnavailable:
      "ਆਨਲਾਈਨ ਬੇਨਤੀਆਂ ਇਸ ਵੇਲੇ ਉਪਲਬਧ ਨਹੀਂ ਹਨ। ਇਸ ਪੰਨੇ ਉੱਤੇ ਦਿੱਤੀ ਸੰਪਰਕ ਜਾਣਕਾਰੀ ਵਰਤੋ।",
    privacy: "ਸਪੁਰਦ ਕਰਕੇ, ਤੁਸੀਂ ਆਪਣੀ ਛੱਤ ਦੀ ਪੁੱਛਗਿੱਛ ਬਾਰੇ ਸੰਪਰਕ ਕੀਤੇ ਜਾਣ ਲਈ ਸਹਿਮਤ ਹੋ।",
    privacyLink: "ਗੋਪਨੀਯਤਾ ਨੀਤੀ",
  },
  finalCta: {
    title: "ਤੁਹਾਡੀ ਛੱਤ ਹੇਠਲੀ ਹਰ ਚੀਜ਼ ਦੀ ਰਾਖੀ ਕਰਦੀ ਹੈ।",
    body: "ਇੱਕ ਮੁਲਾਂਕਣ ਨਾਲ ਸ਼ੁਰੂ ਕਰੋ। ਤੁਸੀਂ ਆਪਣੀ ਛੱਤ ਦੀ ਸਥਿਤੀ, ਉਪਲਬਧ ਵਿਕਲਪਾਂ ਅਤੇ ਹਰ ਇੱਕ ਵਿੱਚ ਕੀ ਸ਼ਾਮਲ ਹੈ ਬਾਰੇ ਜਾਣੋਗੇ।",
  },
  footer: {
    servicesTitle: "ਸੇਵਾਵਾਂ",
    areasTitle: "ਸੇਵਾ ਖੇਤਰ",
    contactTitle: "ਸੰਪਰਕ ਕਰੋ",
    privacy: "ਗੋਪਨੀਯਤਾ",
    terms: "ਸ਼ਰਤਾਂ",
    rights: "ਸਾਰੇ ਹੱਕ ਰਾਖਵੇਂ ਹਨ.",
    disclaimer:
      "ਇਸ ਸਾਈਟ 'ਤੇ ਛੱਤ ਮਾਰਗਦਰਸ਼ਨ ਆਮ ਜਾਣਕਾਰੀ ਹੈ, ਨਾ ਕਿ ਇੰਜੀਨੀਅਰਿੰਗ ਰਾਏ। ਆਨ-ਸਾਈਟ ਮੁਲਾਂਕਣ ਤੋਂ ਬਾਅਦ ਸਿਫ਼ਾਰਸ਼ਾਂ ਦੀ ਪੁਸ਼ਟੀ ਕੀਤੀ ਜਾਂਦੀ ਹੈ।",
  },
  sticky: {
    call: "ਕਾਲ",
    quote: "ਕੋਟ ਲਵੋ",
  },
};

/** Korean */
export const ko: Deep<Dictionary> = {
  meta: {
    title: "Revolin Roofing | 메트로 밴쿠버 프리미엄 지붕 시공",
    description:
      "메트로 밴쿠버 주택 소유주를 위한 지붕 교체, 수리 및 프리미엄 지붕 시스템. 정밀 진단과 투명한 견적.",
  },
  nav: {
    services: "서비스",
    roofTypes: "지붕 종류",
    beforeAfter: "시공 전후",
    areas: "서비스 지역",
    why: "Revolin 선택 이유",
    faq: "자주 묻는 질문",
    contact: "문의",
    menu: "메뉴",
    close: "메뉴 닫기",
    language: "언어",
  },
  cta: {
    primary: "지붕 진단 신청",
    call: "지금 전화",
    callBrand: "Revolin Roofing에 전화",
    learnMore: "자세히 보기",
  },
  hero: {
    eyebrow: "메트로 밴쿠버 · 로워 메인랜드",
    titleLine1: "BC 기후에 맞춘 지붕.",
    titleLine2: "Revolin 기준으로 마감.",
    body: "Revolin Roofing은 메트로 밴쿠버 주택 소유주를 위해 지붕 교체, 수리, 프리미엄 지붕 시스템을 제공합니다. 해안성 강우에 대응하고 건축 디테일을 존중하며 Revolin Group의 관리 기준으로 시공합니다.",
    imageCaption: "스탠딩 심 금속 지붕, 노스쇼어 건축",
  },
  trust: {
    eyebrow: "보장 및 자격",
    title: "보험·등록·보증이 갖춰진 시공",
    guaranteeMention:
      "모든 작업에 서면 시공 보증이 제공됩니다. 전체 보증 내용은 소개 페이지에서 확인하세요.",
    guaranteeLink: "보증 안내",
  },
  brands: {
    eyebrow: "자재",
    title: "신뢰할 수 있는 브랜드를 시공합니다",
    seeAll: "사용하는 자재 보기",
  },
  testimonials: {
    eyebrow: "후기",
    title: "주택 소유자의 이야기",
    previewNotice: "미리보기 플레이스홀더 - 프로덕션에서는 표시되지 않음",
    googleCta: "Google에서 후기 더 보기:",
    googleLinkLabel: "Google 후기",
  },
  services: {
    eyebrow: "서비스",
    title: "메트로 밴쿠버 주택을 위한 지붕 공사 서비스",
    body: "긴급 누수부터 전체 건축물 재지붕까지 모든 프로젝트는 동일한 평가 원칙으로 시작됩니다.",
    items: [
      {
        title: "지붕 교체",
        intent: "내 지붕의 수명이 다 되었습니다",
        body: "기존 지붕을 완전히 철거하고 주택에 맞는 시스템, 방수 하부층, 환기 및 플래싱 디테일로 새 지붕을 시공합니다.",
      },
      {
        title: "지붕 수리 및 누수 진단",
        intent: "누출이 있거나 눈에 보이는 손상이 있습니다.",
        body: "수리 범위를 제안하기 전에 플래싱, 관통부 및 배수와 같은 일반적인 누수 경로를 점검합니다.",
      },
      {
        title: "건축용 아스팔트 지붕널",
        intent: "검증된 일반적인 지붕 시스템을 원합니다",
        body: "다양한 프로파일과 색상을 갖춘 경사진 지붕을 위한 강력한 주거용 옵션입니다.",
      },
      {
        title: "스탠딩 심 금속 지붕",
        intent: "긴 수명과 현대적인 디자인을 원합니다",
        body: "숨겨진 패스너와 선명한 라인을 갖춘 현대 및 서해안 건축물을 위한 프리미엄 옵션입니다.",
      },
      {
        title: "평탄하고 낮은 경사의 지붕",
        intent: "내 지붕은 평평하거나 경사가 낮습니다.",
        body: "난간, 연석 및 전환 부분의 배수, 경사 및 디테일링을 위해 지정된 멤브레인 시스템입니다.",
      },
      {
        title: "지붕 검사 및 유지 관리",
        intent: "현재 지붕 상태를 알고 싶습니다",
        body: "정보에 입각한 계획을 지원하기 위해 문서화된 상태 평가 및 유지 관리 우선순위를 제공합니다.",
      },
    ],
  },
  comparison: {
    eyebrow: "지붕 시스템",
    title: "당신의 집에는 어떤 지붕이 적합할까요?",
    body: "모든 주택에 가장 좋은 단일 지붕 자재는 없습니다. 적합한 시스템은 경사, 건축, 노출 조건, 배수, 예산 및 해당 지역 요구 사항에 따라 달라집니다. 주요 선택지를 일반적으로 비교하면 다음과 같습니다.",
    columns: {
      application: "이상적인 적용",
      character: "디자인 특성",
      maintenance: "유지 관리 고려 사항",
      investment: "상대적 비용",
      performance: "성능 특성",
    },
    systems: [
      {
        name: "건축용 아스팔트 지붕널",
        summary: "강력한 주류 주거 옵션입니다.",
        application: "대부분의 Lower Mainland 주택의 경사진 지붕.",
        character: "전통적에서 과도기적 차원의 질감.",
        maintenance: "정기검사 해안 기후에서는 이끼 통제가 중요합니다.",
        investment: "중간",
        performance: "광범위한 제품 가용성과 간단한 향후 수리 기능을 갖춘 잘 알려진 시스템입니다.",
      },
      {
        name: "스탠딩 심 메탈",
        summary: "프리미엄 수명과 디자인 옵션.",
        application: "깨끗한 지붕 평면을 갖춘 현대 및 서해안 건축물.",
        character: "선명하고 선형적이며 건축적입니다.",
        maintenance: "일상적인 유지 관리가 적습니다. 디테일과 고정 품질이 중요합니다.",
        investment: "높음",
        performance: "숨겨진 패스너와 연속 패널은 노출된 높이에서 물을 잘 배출합니다.",
      },
      {
        name: "평지붕 및 저경사 멤브레인",
        summary: "낮은 경사 지역에 적합한 시스템 제품군입니다.",
        application: "평평하고 낮은 경사의 지붕 구역, 데크 및 추가 시설.",
        character: "절제된 외관: 지붕이 시각적으로 두드러지지 않습니다.",
        maintenance: "배수 및 솔기 검사; 배수로를 깨끗하게 유지하십시오.",
        investment: "멤브레인 및 어셈블리에 따라 다름",
        performance: "성능은 배수구 경사, 멤브레인 선택 및 전환 세부 사항에 따라 크게 달라집니다.",
      },
    ],
    disclaimer:
      "안내만 가능합니다. 재료 선택 및 조립 세부 사항은 현재 RCABC 지침과 해당 BC 및 지방 자치 단체 요구 사항을 따릅니다. 우리는 엔지니어링 보증을 제공하지 않습니다.",
  },
  why: {
    eyebrow: "왜 레볼린 루핑인가?",
    title: "건설회사의 지붕사업부",
    body: "Revolin Roofing은 주거용 지붕에 Revolin Group의 프로젝트 접근 방식을 적용합니다. 먼저 평가하고 제안 범위를 서면으로 작성하며 해안 날씨 세부 사항에 주의를 기울입니다.",
    items: [
      {
        title: "추천 전 평가",
        body: "우리는 범위를 제안하기 전에 우리가 찾은 내용을 문서화합니다.",
      },
      {
        title: "투명한 범위와 가격",
        body: "제안된 범위는 변경이 필요할 수 있는 포함, 제외 및 조건을 식별합니다.",
      },
      {
        title: "건축학적 관심",
        body: "지붕선, 색상 및 프로필은 건물에 맞게 선택됩니다.",
      },
      {
        title: "플래싱 및 전환 세부 사항",
        body: "전환은 일반적인 조사 지점이므로 평가 및 범위 지정 중에 의도적인 주의를 받습니다.",
      },
      {
        title: "부지 계획",
        body: "각 주택에 맞춰 접근, 보호 및 청소 기준을 계획합니다.",
      },
      {
        title: "프로젝트 커뮤니케이션",
        body: "일정 및 의사소통 기대치는 프로젝트 범위에 따라 확정됩니다.",
      },
      {
        title: "품질 관리 체크포인트",
        body: "설치 중 및 설치 종료 시 정의된 검사입니다.",
      },
      {
        title: "모회사 전문성",
        body: "지붕 문제가 단순한 지붕 문제가 아닌 경우 Revolin Group의 광범위한 건설 역량에 접근할 수 있습니다.",
      },
    ],
  },
  beforeAfter: {
    eyebrow: "시공 전후",
    title: "디테일의 차이를 확인해보세요",
    body: "핸들을 드래그해 메트로 밴쿠버 주택의 시공 전후 변화를 비교해 보세요.",
    beforeLabel: "시공 전",
    afterLabel: "시공 후",
    sliderLabel: "전후 비교 슬라이더",
  },
  process: {
    eyebrow: "프로세스",
    title: "Revolin 지붕 프로젝트 실행 방법",
    steps: [
      {
        n: "01",
        title: "지붕 평가",
        body: "우리는 지붕, 후레싱, 배수 및 내부 증거를 검사하고 상태를 문서화합니다.",
      },
      {
        n: "02",
        title: "범위 및 지붕 권장 사항",
        body: "제안서는 검토를 위한 적절한 옵션, 추론, 범위 및 가격을 문서화할 수 있습니다.",
      },
      {
        n: "03",
        title: "설치/수리",
        body: "승인된 작업은 문서화된 범위, 현장 계획 및 해당 프로젝트 요구 사항에 따라 진행됩니다.",
      },
      {
        n: "04",
        title: "최종 검사 및 인도",
        body: "완료 요구 사항 및 프로젝트 문서는 합의된 범위를 따릅니다.",
      },
    ],
  },
  weather: {
    eyebrow: "BC 조건",
    title: "이곳의 지붕은 물 관리 시스템입니다",
    body: "로어 메인랜드(Lower Mainland)에서는 지붕이 일년 중 많은 시간을 태양보다는 장기간의 비를 관리하는 데 사용합니다. 그러면 중요한 것이 달라집니다.",
    items: [
      {
        title: "장기간의 비와 습기",
        body: "습한 해안 기후에서는 방수 하부층, 겹침부 및 환기가 더욱 중요합니다.",
      },
      {
        title: "바람과 노출",
        body: "노출된 입면과 개방된 부지는 고정 및 가장자리 디테일에 영향을 미칩니다.",
      },
      {
        title: "배수",
        body: "배수구 경사, 홈통 및 빗물홈통 용량에 따라 물이 부하를 받는 위치가 결정됩니다.",
      },
      {
        title: "해당하는 경우 눈",
        body: "이 지역의 고도가 높을수록 평평한 지역에서는 볼 수 없는 적설량과 해빙수 순환이 나타납니다.",
      },
      {
        title: "지붕 기하학",
        body: "계곡, 지붕창, 채광창 및 추가 시설은 가장 주의가 필요한 전환을 만듭니다.",
      },
    ],
  },
  areas: {
    eyebrow: "서비스 지역",
    title: "메트로 밴쿠버와 로어 메인랜드 서비스 제공",
    body: "귀하의 커뮤니티가 근처에 있지만 목록에 없는 경우 당사에 연락하시면 이용 가능 여부를 확인해 드리겠습니다.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "주택 소유자 질문",
    items: [
      {
        q: "지붕 수리가 필요합니까, 아니면 전체 교체가 필요합니까?",
        a: "이는 조립 연식과 상태, 그리고 문제가 고립되었는지 여부에 따라 달라집니다. 국부적인 문제는 수리 범위를 지원할 수 있는 반면, 오래된 문제나 여러 오류 지점은 교체를 고려하는 것이 타당할 수 있습니다. 평가는 관찰된 조건에 맞는 옵션을 문서화하는 데 도움이 됩니다.",
      },
      {
        q: "지붕이 새고 있어요. 먼저 무엇을 해야 하나요?",
        a: "실내로 들어온 물을 받아 두고 안전하게 확인할 수 있는 부분을 촬영한 뒤 연락해 주세요. 젖은 지붕에는 올라가지 마십시오. 누수 원인은 눈에 보이는 얼룩과 떨어진 곳에 있을 수 있으므로 단순 보수보다 정확한 진단이 중요합니다.",
      },
      {
        q: "지붕 평가 중에는 어떤 일이 발생합니까?",
        a: "우리는 지붕 덮개, 후레싱, 관통부, 환기 및 배수를 검토하고 관련 있는 내부 증거를 찾고 결과를 문서화합니다. 그런 다음 서면 범위와 권장 사항을 받게 됩니다.",
      },
      {
        q: "어떤 지붕 재료가 가장 좋습니까?",
        a: "모든 집에 가장 적합한 재료는 없습니다. 경사, 건축, 노출, 배수, 예산 및 적용 가능한 현지 요구 사항이 모두 고려됩니다. 우리는 장단점을 설명하고 하나의 제품을 밀기보다는 조립을 권장합니다.",
      },
      {
        q: "평탄하고 경사가 낮은 지붕에서 작업할 수 있습니까?",
        a: "예. 낮은 경사 지역은 멤브레인 시스템으로 지정되며 특히 배수구 경사와 난간, 연석 및 전환 부분의 세부 사항에 주의를 기울입니다.",
      },
      {
        q: "금속 지붕과 아스팔트 싱글 중 무엇이 좋나요?",
        a: "건축용 아스팔트 지붕널은 강력한 주류 주거 옵션입니다. 스탠딩 심 메탈은 현대 및 서해안 건축물에 적합한 프리미엄 수명 및 디자인 옵션입니다. 둘 다 정확할 수 있습니다. 이는 건물과 우선순위에 따라 다릅니다.",
      },
      {
        q: "지붕 공사는 얼마나 걸리나요?",
        a: "타임라인은 크기, 복잡성, 액세스 및 선택한 시스템에 따라 다릅니다. 귀하의 범위에 예상 기간을 제공하고 조건이 변경되면 업데이트해 드립니다.",
      },
      {
        q: "견적은 어떻게 받나요?",
        a: "지붕 평가를 요청하세요. 지붕을 살펴본 후 서면 범위와 가격을 제공합니다. 우리는 지붕을 보지 않고 지붕 재설치를 견적하지 않습니다.",
      },
      {
        q: "프로젝트 도중 비가 오면 어떻게 되나요?",
        a: "날씨는 안전한 작업, 순서 및 일정에 영향을 미칠 수 있습니다. 보호 접근 방식과 기상 관련 변경 사항은 프로젝트별 계획 및 커뮤니케이션 프로세스를 통해 처리됩니다.",
      },
      {
        q: "어떤 보증이 적용되나요?",
        a: "제조업체 적용 범위는 제품에 따라 다르며 기술 조건은 서면 범위에서 확인됩니다. 여기에는 보증 조건을 게시하지 않습니다. 귀하의 프로젝트에 적용되는 조건은 귀하의 문서에 명시된 조건입니다.",
      },
      {
        q: "어느 지역에서 봉사하시나요?",
        a: "이 페이지에 나열된 커뮤니티를 포함한 메트로 밴쿠버 및 로어 메인랜드. 근처에서 이용 가능 여부를 확인하려면 저희에게 연락하세요.",
      },
    ],
  },
  form: {
    eyebrow: "평가 요청",
    title: "지붕 진단 신청",
    body: "해당 부동산, 지붕 및 귀하가 발견한 사항에 대해 알려주십시오.",
    name: "이름",
    phone: "전화",
    email: "이메일",
    postal: "우편번호",
    need: "필요한 지붕 서비스",
    needOptions: [
      "지붕교체",
      "지붕 수리 / 누수",
      "평탄하거나 낮은 경사의 지붕",
      "지붕 검사",
      "아직 확실하지 않음",
    ],
    timeline: "희망 일정",
    timelineOptions: ["가능한 한 빨리", "1~3개월 이내", "3~6개월", "계획 단계"],
    message: "문제 설명(선택사항)",
    submit: "지붕 진단 신청",
    submitting: "전송 중…",
    successTitle: "요청 접수됨",
    successBody: "감사합니다. 귀하의 지붕 평가 요청이 성공적으로 전달되었습니다.",
    errorRequired: "이 필드는 필수입니다",
    errorEmail: "유효한 이메일 주소를 입력하세요.",
    errorPhone: "유효한 전화번호를 입력하세요.",
    errorSubmit: "요청을 보내지 못했습니다. 이 페이지의 연락처를 이용해 주세요.",
    errorUnavailable: "온라인 요청을 현재 이용할 수 없습니다. 이 페이지의 연락처를 이용해 주세요.",
    privacy: "제출하시면 지붕 공사 문의에 관해 연락을 받는 데 동의하는 것입니다.",
    privacyLink: "개인정보 처리방침",
  },
  finalCta: {
    title: "지붕은 그 아래의 모든 것을 지킵니다.",
    body: "진단부터 시작하세요. 지붕 상태와 선택지, 각 방식의 내용을 명확히 알려드립니다.",
  },
  footer: {
    servicesTitle: "서비스",
    areasTitle: "서비스 지역",
    contactTitle: "문의",
    privacy: "개인정보 처리방침",
    terms: "이용약관",
    rights: "모든 권리 보유.",
    disclaimer:
      "이 사이트의 지붕 설치 지침은 엔지니어링 의견이 아닌 일반적인 정보입니다. 권장 사항은 현장 평가 후 확정됩니다.",
  },
  sticky: {
    call: "전화",
    quote: "견적 요청",
  },
};

/** Persian (Farsi) — RTL; formal homeowner-facing copy for Metro Vancouver. */
export const fa: Deep<Dictionary> = {
  meta: {
    title: "رولین روفینگ | سقف‌سازی حرفه‌ای در مترو ونکوور، بریتیش کلمبیا",
    description:
      "تعویض و تعمیر انواع سقف برای صاحبان خانه در مترو ونکوور؛ با ارزیابی دقیق، شرح کار شفاف و استانداردهای گروه رولین.",
  },
  nav: {
    services: "خدمات",
    roofTypes: "انواع سقف",
    beforeAfter: "قبل و بعد",
    areas: "مناطق خدماتی",
    why: "چرا رولین؟",
    faq: "پرسش‌های متداول",
    contact: "تماس",
    menu: "منو",
    close: "بستن منو",
    language: "زبان",
  },
  cta: {
    primary: "درخواست ارزیابی سقف",
    call: "همین حالا تماس بگیرید",
    callBrand: "تماس با رولین روفینگ",
    learnMore: "بیشتر بدانید",
  },
  hero: {
    eyebrow: "مترو ونکوور · لوئر مینلند",
    titleLine1: "سقفی برای اقلیم بریتیش کلمبیا.",
    titleLine2: "اجرا با استانداردهای رولین.",
    body: "رولین روفینگ خدمات تعویض و تعمیر انواع سقف را به صاحبان خانه در مترو ونکوور ارائه می‌دهد. پیشنهاد ما بر پایهٔ ویژگی‌های خانه، وضعیت واقعی سقف و الزامات همان پروژه تنظیم می‌شود.",
    imageCaption: "سقف فلزی درز ایستاده در معماری نورث‌شور",
  },
  trust: {
    eyebrow: "پوشش بیمه و مجوزها",
    title: "کاری بیمه‌شده، ثبت‌شده و با ضمانت‌نامه",
    guaranteeMention:
      "ضمانت‌نامهٔ کیفیت اجرا برای هر پروژه به‌صورت مکتوب صادر می‌شود. جزئیات کامل ضمانت را در صفحهٔ «درباره ما» بخوانید.",
    guaranteeLink: "ضمانت ما",
  },
  brands: {
    eyebrow: "مصالح",
    title: "برندهای مورد اعتماد نصب می‌کنیم",
    seeAll: "مصالح مورد اعتماد ما را ببینید",
  },
  testimonials: {
    eyebrow: "نظرات مشتریان",
    title: "صاحبان خانه چه می‌گویند",
    previewNotice: "پیش‌نمایش آزمایشی - در نسخهٔ نهایی نمایش داده نمی‌شود",
    googleCta: "نظرات بیشتر را در Google بخوانید:",
    googleLinkLabel: "نظرات Google",
  },
  services: {
    eyebrow: "خدمات",
    title: "خدمات سقف برای خانه‌های مترو ونکوور",
    body: "از تعمیر یک نشتی فوری تا تعویض کامل سقف، هر پروژه با همان روال دقیق ارزیابی آغاز می‌شود.",
    items: [
      {
        title: "تعویض کامل سقف",
        intent: "سقف خانه‌ام به پایان عمر مفید خود رسیده است",
        body: "برچیدن کامل پوشش قدیمی و اجرای سقف جدید با سیستم، زیرلایه، تهویه و جزئیات فلاشینگ متناسب با خانهٔ شما.",
      },
      {
        title: "تعمیر سقف و تشخیص نشتی",
        intent: "نشتی یا آسیب قابل‌مشاهده دارم",
        body: "پیش از ارائهٔ شرح کار تعمیر، مسیرهای رایج نشتی مانند فلاشینگ، نقاط عبور تأسیسات و زهکشی بررسی می‌شود.",
      },
      {
        title: "شینگل آسفالتی معماری",
        intent: "سقفی رایج و امتحان‌پس‌داده می‌خواهم",
        body: "گزینه‌ای مطمئن برای سقف‌های شیب‌دار مسکونی، با تنوع گسترده‌ای از طرح و رنگ.",
      },
      {
        title: "سقف فلزی درز ایستاده",
        intent: "دوام بالا و ظاهری مدرن می‌خواهم",
        body: "گزینه‌ای ممتاز برای معماری مدرن و سبک ساحل غربی، با اتصالات پنهان و خطوط تمیز.",
      },
      {
        title: "سقف تخت و کم‌شیب",
        intent: "سقف خانهٔ من تخت یا کم‌شیب است",
        body: "سیستم‌های غشایی که بر اساس زهکشی، شیب و جزئیات جان‌پناه، کرب و نقاط انتقال طراحی می‌شوند.",
      },
      {
        title: "بازرسی و نگهداری سقف",
        intent: "می‌خواهم از وضعیت سقفم مطمئن شوم",
        body: "ارزیابی مستند وضعیت سقف و اولویت‌های نگهداری، برای تصمیم‌گیری آگاهانه.",
      },
    ],
  },
  comparison: {
    eyebrow: "سیستم‌های سقف",
    title: "کدام سقف برای خانهٔ شما مناسب است؟",
    body: "هیچ مصالح سقفی برای همهٔ خانه‌ها بهترین نیست. انتخاب درست به شیب، معماری، میزان قرارگیری در معرض باد و باران، زهکشی، بودجه و الزامات محلی بستگی دارد. مقایسهٔ کلی گزینه‌های اصلی را در ادامه می‌بینید.",
    columns: {
      application: "کاربرد ایده‌آل",
      character: "حال‌وهوای طراحی",
      maintenance: "ملاحظات نگهداری",
      investment: "هزینهٔ نسبی",
      performance: "ویژگی‌های عملکردی",
    },
    systems: [
      {
        name: "شینگل آسفالتی معماری",
        summary: "گزینهٔ رایج و مطمئن برای خانه‌های مسکونی.",
        application: "سقف‌های شیب‌دار بیشتر خانه‌های لوئر مینلند.",
        character: "بافت برجسته؛ از کلاسیک تا نیمه‌مدرن.",
        maintenance: "بازرسی دوره‌ای؛ در اقلیم ساحلی، کنترل خزه اهمیت زیادی دارد.",
        investment: "متوسط",
        performance:
          "سیستمی شناخته‌شده با دسترسی گستردهٔ محصولات؛ تعمیرات آینده نیز نسبتاً ساده است.",
      },
      {
        name: "سقف فلزی درز ایستاده",
        summary: "گزینهٔ ممتاز از نظر دوام و طراحی.",
        application: "معماری مدرن و سبک ساحل غربی با سطوح سقف یکدست.",
        character: "خطوطی شفاف، مستقیم و معمارانه.",
        maintenance: "نگهداری روزمرهٔ کم؛ کیفیت جزئیات و اتصالات تعیین‌کننده است.",
        investment: "بالاتر",
        performance:
          "اتصالات پنهان و پنل‌های یکپارچه، آب را در نماهای بادگیر به‌خوبی هدایت می‌کنند.",
      },
      {
        name: "غشای تخت و کم‌شیب",
        summary: "خانوادهٔ مناسب سیستم‌ها برای بخش‌های کم‌شیب.",
        application: "بخش‌های تخت و کم‌شیب سقف، تراس‌ها و الحاقات.",
        character: "بی‌ادعا: سقف در این حالت عنصر بصری اصلی نیست.",
        maintenance: "بازرسی آبروها و درزها؛ مسیرهای زهکشی باید همیشه باز بمانند.",
        investment: "بسته به نوع غشا و سیستم متفاوت است",
        performance:
          "عملکرد به شیب‌بندی به‌سمت آبرو، انتخاب غشا و جزئیات نقاط انتقال بستگی زیادی دارد.",
      },
    ],
    disclaimer:
      "این جدول صرفاً راهنمای کلی است. انتخاب مصالح و جزئیات اجرا مطابق رهنمودهای جاری RCABC و الزامات استان بریتیش کلمبیا و شهرداری‌ها انجام می‌شود. ما ضمانت مهندسی ارائه نمی‌دهیم.",
  },
  why: {
    eyebrow: "چرا رولین روفینگ؟",
    title: "بخش سقف‌سازی یک شرکت ساختمانی",
    body: "رولین روفینگ رویکرد پروژه‌محور گروه رولین را در اجرای سقف‌های مسکونی به کار می‌گیرد: ابتدا ارزیابی دقیق، سپس شرح کار مکتوب و توجه ویژه به جزئیاتی که اقلیم ساحلی ایجاب می‌کند.",
    items: [
      {
        title: "ارزیابی، پیش از پیشنهاد",
        body: "پیش از آنکه شرح کاری پیشنهاد کنیم، یافته‌های خود را مستند می‌کنیم.",
      },
      {
        title: "شرح کار و قیمت شفاف",
        body: "در شرح کار پیشنهادی، موارد شامل، موارد مستثنا و شرایطی که ممکن است به تغییر نیاز داشته باشد مشخص می‌شود.",
      },
      {
        title: "نگاه معمارانه",
        body: "خطوط سقف، رنگ و طرح متناسب با ساختمان انتخاب می‌شوند.",
      },
      {
        title: "جزئیات فلاشینگ و نقاط انتقال",
        body: "نقاط انتقال از محل‌های رایج بروز مشکل‌اند؛ به همین دلیل در ارزیابی و تنظیم شرح کار با دقت ویژه بررسی می‌شوند.",
      },
      {
        title: "برنامه‌ریزی محل کار",
        body: "دسترسی، حفاظت از ملک و نظافت پایان کار برای هر ملک به‌طور مشخص برنامه‌ریزی می‌شود.",
      },
      {
        title: "اطلاع‌رسانی در طول پروژه",
        body: "زمان‌بندی و نحوهٔ اطلاع‌رسانی همراه با شرح کار پروژه تأیید می‌شود.",
      },
      {
        title: "ایستگاه‌های کنترل کیفیت",
        body: "بازبینی‌های مشخص در حین نصب و در پایان کار.",
      },
      {
        title: "پشتوانهٔ شرکت مادر",
        body: "وقتی مشکل فقط به سقف محدود نیست، توان اجرایی گسترده‌تر گروه رولین نیز در دسترس شماست.",
      },
    ],
  },
  beforeAfter: {
    eyebrow: "قبل و بعد",
    title: "تفاوت را در جزئیات ببینید",
    body: "دستگیره را بکشید و تصاویر قبل و بعد سقف خانه‌های مترو ونکوور را مقایسه کنید.",
    beforeLabel: "قبل",
    afterLabel: "بعد",
    sliderLabel: "اسلایدر مقایسهٔ قبل و بعد",
  },
  process: {
    eyebrow: "روند کار",
    title: "روند اجرای پروژهٔ سقف در رولین",
    steps: [
      {
        n: "01",
        title: "ارزیابی سقف",
        body: "سقف، فلاشینگ‌ها، زهکشی و نشانه‌های داخل ساختمان را بررسی و وضعیت را مستند می‌کنیم.",
      },
      {
        n: "02",
        title: "شرح کار و پیشنهاد",
        body: "در پیشنهاد، گزینه‌های مناسب، دلیل انتخاب، شرح کار و قیمت برای بررسی شما مستند می‌شود.",
      },
      {
        n: "03",
        title: "نصب / تعمیر",
        body: "کار پس از تأیید، مطابق شرح کار مکتوب، برنامهٔ محل کار و الزامات پروژه پیش می‌رود.",
      },
      {
        n: "04",
        title: "بازرسی نهایی و تحویل",
        body: "تکمیل کار و مستندات پروژه مطابق شرح کار توافق‌شده انجام می‌شود.",
      },
    ],
  },
  weather: {
    eyebrow: "شرایط بریتیش کلمبیا",
    title: "سقف در این منطقه یک سیستم مدیریت آب است",
    body: "در لوئر مینلند، سقف بیشترِ سال درگیر باران‌های طولانی است، نه آفتاب؛ و همین، اولویت‌ها را عوض می‌کند.",
    items: [
      {
        title: "باران طولانی و رطوبت",
        body: "در اقلیم مرطوب ساحلی، زیرلایه، هم‌پوشانی‌ها و تهویه اهمیت بیشتری پیدا می‌کنند.",
      },
      {
        title: "باد و بادگیر بودن",
        body: "نماهای بادگیر و زمین‌های باز بر نحوهٔ اتصال و جزئیات لبه‌ها اثر می‌گذارند.",
      },
      {
        title: "زهکشی",
        body: "شیب‌بندی به‌سمت آبرو، ناودان‌ها و ظرفیت لوله‌های ناودان تعیین می‌کنند آب در باران سنگین کجا می‌رود.",
      },
      {
        title: "برف، هرجا که مطرح باشد",
        body: "نقاط مرتفع‌تر منطقه بار برف و چرخه‌های ذوب را تجربه می‌کنند؛ چیزی که در زمین‌های پست دیده نمی‌شود.",
      },
      {
        title: "هندسهٔ سقف",
        body: "دره‌ها، پنجره‌های شیروانی، نورگیرها و الحاقات همان نقاط انتقالی را می‌سازند که بیشترین دقت را می‌طلبند.",
      },
    ],
  },
  areas: {
    eyebrow: "مناطق خدماتی",
    title: "در خدمت مترو ونکوور و لوئر مینلند",
    body: "اگر محلهٔ شما نزدیک است اما در فهرست نیامده، با ما تماس بگیرید تا امکان خدمت‌رسانی را تأیید کنیم.",
  },
  faq: {
    eyebrow: "پرسش‌های متداول",
    title: "پرسش‌های صاحبان خانه",
    items: [
      {
        q: "تعمیر سقف کافی است یا باید کامل تعویض شود؟",
        a: "به عمر و وضعیت سقف و موضعی بودن مشکل بستگی دارد. یک مشکل محدود معمولاً با تعمیر موضعی برطرف می‌شود، اما پوشش فرسوده یا چند نقطهٔ خرابی ممکن است تعویض کامل را منطقی کند. ارزیابی کمک می‌کند گزینه‌های متناسب با شرایط واقعی سقف مستند شوند.",
      },
      {
        q: "سقفم نشتی دارد. اول چه کار کنم؟",
        a: "جلوی پخش آب را در داخل خانه بگیرید، از هر چیزی که بدون خطر می‌بینید عکس بگیرید و با ما تماس بگیرید. روی سقف خیس نروید. نشتی اغلب از نقطه‌ای دورتر از لکهٔ مرئی شروع می‌شود؛ برای همین، تشخیص درست مهم‌تر از وصلهٔ موقت است.",
      },
      {
        q: "در ارزیابی سقف چه اتفاقی می‌افتد؟",
        a: "پوشش سقف، فلاشینگ‌ها، نقاط عبور تأسیسات، تهویه و زهکشی را بررسی می‌کنیم، در صورت نیاز نشانه‌های داخل ساختمان را هم می‌بینیم و یافته‌ها را مستند می‌کنیم. سپس شرح کار مکتوب و پیشنهاد ما را دریافت می‌کنید.",
      },
      {
        q: "بهترین مصالح سقف کدام است؟",
        a: "هیچ مصالحی برای همهٔ خانه‌ها بهترین نیست. شیب، معماری، بادگیر بودن، زهکشی، بودجه و الزامات محلی همگی نقش دارند. ما مزایا و معایب هر گزینه را توضیح می‌دهیم و سیستم مناسب را پیشنهاد می‌کنیم، نه اینکه یک محصول خاص را تحمیل کنیم.",
      },
      {
        q: "روی سقف‌های تخت و کم‌شیب هم کار می‌کنید؟",
        a: "بله. برای بخش‌های کم‌شیب، سیستم غشایی مشخص می‌شود؛ با توجه ویژه به شیب‌بندی به‌سمت آبرو و جزئیات جان‌پناه، کرب و نقاط انتقال.",
      },
      {
        q: "سقف فلزی یا شینگل؟",
        a: "شینگل آسفالتی معماری گزینه‌ای رایج و مطمئن برای خانه‌های مسکونی است. فلز ایستاده گزینه‌ای ممتاز از نظر دوام و طراحی است و به معماری مدرن و سبک ساحل غربی می‌آید. هر دو می‌توانند انتخاب درستی باشند - بستگی به ساختمان و اولویت‌های شما دارد.",
      },
      {
        q: "پروژهٔ سقف چقدر طول می‌کشد؟",
        a: "زمان‌بندی به اندازه، پیچیدگی، دسترسی و سیستم انتخابی بستگی دارد. مدت‌زمان موردانتظار را همراه با شرح کار اعلام می‌کنیم و اگر شرایط تغییر کند، شما را در جریان می‌گذاریم.",
      },
      {
        q: "چطور قیمت بگیرم؟",
        a: "درخواست ارزیابی سقف ثبت کنید. سقف را از نزدیک می‌بینیم و سپس شرح کار مکتوب و قیمت ارائه می‌کنیم. بدون دیدن سقف، برای تعویض کامل قیمت نمی‌دهیم.",
      },
      {
        q: "اگر وسط پروژه باران ببارد چه می‌شود؟",
        a: "هوا می‌تواند بر ایمنی کار، ترتیب مراحل و زمان‌بندی اثر بگذارد. نحوهٔ حفاظت از سقف و هر تغییر ناشی از هوا، از طریق برنامه و روال اطلاع‌رسانی همان پروژه مدیریت می‌شود.",
      },
      {
        q: "چه ضمانتی دارد؟",
        a: "پوشش ضمانت سازنده بسته به محصول متفاوت است و شرایط ضمانت اجرا در شرح کار مکتوب شما تأیید می‌شود. شرایط ضمانت را اینجا منتشر نمی‌کنیم: آنچه برای پروژهٔ شما معتبر است، همان مفاد اسناد پروژهٔ شماست.",
      },
      {
        q: "به کدام مناطق خدمات می‌دهید؟",
        a: "مترو ونکوور و لوئر مینلند، از جمله محله‌های فهرست‌شده در همین صفحه. برای اطمینان از پوشش محلهٔ خود با ما تماس بگیرید.",
      },
    ],
  },
  form: {
    eyebrow: "درخواست ارزیابی",
    title: "درخواست ارزیابی سقف",
    body: "دربارهٔ ملک، سقف و آنچه متوجه شده‌اید برایمان بنویسید.",
    name: "نام و نام خانوادگی",
    phone: "تلفن",
    email: "ایمیل",
    postal: "کد پستی",
    need: "نوع نیاز شما",
    needOptions: [
      "نشتی فوری / ورود فعال آب",
      "تعمیر سقف / فلاشینگ",
      "تعویض شینگل آسفالتی",
      "سقف فلزی",
      "سقف تخت یا کم‌شیب",
      "سقف چوبی سدار",
      "ناودان / فاسیا / تهویه",
      "خزه‌زدایی / نگهداری",
      "بازرسی و بررسی سلامت سقف",
      "هنوز مطمئن نیستم",
    ],
    timeline: "زمان‌بندی موردنظر",
    timelineOptions: ["در اسرع وقت", "طی ۱ تا ۳ ماه", "۳ تا ۶ ماه", "فعلاً فقط برنامه‌ریزی"],
    message: "شرح مشکل (اختیاری)",
    submit: "درخواست ارزیابی سقف",
    submitting: "در حال ارسال…",
    successTitle: "درخواست شما دریافت شد",
    successBody: "متشکریم. درخواست ارزیابی سقف شما با موفقیت ثبت شد.",
    errorRequired: "پر کردن این فیلد الزامی است",
    errorEmail: "یک ایمیل معتبر وارد کنید",
    errorPhone: "یک شماره تلفن معتبر وارد کنید",
    errorSubmit: "ارسال درخواست ممکن نشد. لطفاً از راه‌های تماس همین صفحه استفاده کنید.",
    errorUnavailable:
      "ثبت درخواست آنلاین موقتاً در دسترس نیست. لطفاً از راه‌های تماس همین صفحه استفاده کنید.",
    privacy: "با ارسال این فرم موافقت می‌کنید که دربارهٔ درخواست سقف‌تان با شما تماس بگیریم.",
    privacyLink: "سیاست حریم خصوصی",
  },
  finalCta: {
    title: "سقف شما از همهٔ آنچه زیر آن است محافظت می‌کند.",
    body: "با یک ارزیابی شروع کنید تا از وضعیت سقف، گزینه‌های پیش رو و جزئیات هر کدام مطلع شوید.",
  },
  footer: {
    servicesTitle: "خدمات",
    areasTitle: "مناطق خدماتی",
    contactTitle: "تماس",
    privacy: "حریم خصوصی",
    terms: "شرایط استفاده",
    rights: "کلیهٔ حقوق محفوظ است.",
    disclaimer:
      "مطالب این وب‌سایت دربارهٔ سقف صرفاً اطلاعات عمومی است، نه نظر مهندسی. پیشنهادها پس از ارزیابی حضوری قطعی می‌شوند.",
  },
  sticky: {
    call: "تماس",
    quote: "درخواست قیمت",
  },
};

/** Arabic (MSA) — RTL; formal homeowner-facing copy for Metro Vancouver. */
export const ar: Deep<Dictionary> = {
  meta: {
    title: "رولين روفينغ | تسقيف احترافي في مترو فانكوفر، كولومبيا البريطانية",
    description:
      "استبدال الأسقف وإصلاحها لأصحاب المنازل في مترو فانكوفر؛ تقييم دقيق ونطاق عمل مكتوب وواضح ومعايير مجموعة رولين.",
  },
  nav: {
    services: "الخدمات",
    roofTypes: "أنواع الأسقف",
    beforeAfter: "قبل وبعد",
    areas: "مناطق الخدمة",
    why: "لماذا رولين؟",
    faq: "الأسئلة الشائعة",
    contact: "تواصل معنا",
    menu: "القائمة",
    close: "إغلاق القائمة",
    language: "اللغة",
  },
  cta: {
    primary: "اطلب تقييم السقف",
    call: "اتصل بنا الآن",
    callBrand: "اتصل برولين روفينغ",
    learnMore: "اعرف المزيد",
  },
  hero: {
    eyebrow: "مترو فانكوفر · لوور مينلاند",
    titleLine1: "تسقيف مصمَّم لمناخ كولومبيا البريطانية.",
    titleLine2: "ويُنجَز وفق معايير رولين.",
    body: "تقدّم رولين روفينغ خدمات استبدال الأسقف وإصلاحها لأصحاب المنازل في مترو فانكوفر، وتضع توصياتها وفق طبيعة المنزل وحالة السقف الفعلية والمتطلبات المنطبقة على المشروع.",
    imageCaption: "سقف معدني بدرزات قائمة في طراز نورث شور المعماري",
  },
  trust: {
    eyebrow: "التغطية والاعتمادات",
    title: "عمل مؤمَّن ومسجَّل ومشمول بالضمان",
    guaranteeMention:
      "نُصدر ضمان جودة التنفيذ كتابياً في كل مشروع. اطّلع على تفاصيل الضمان كاملة في صفحة «من نحن».",
    guaranteeLink: "ضماننا",
  },
  brands: {
    eyebrow: "المواد",
    title: "نركّب علامات تجارية موثوقة",
    seeAll: "اطّلع على المواد التي نثق بها",
  },
  testimonials: {
    eyebrow: "آراء العملاء",
    title: "ماذا يقول أصحاب المنازل",
    previewNotice: "عناصر تجريبية للمعاينة - لا تظهر في النسخة النهائية",
    googleCta: "اقرأ المزيد من المراجعات على Google:",
    googleLinkLabel: "مراجعات Google",
  },
  services: {
    eyebrow: "الخدمات",
    title: "خدمات تسقيف لمنازل مترو فانكوفر",
    body: "من تسرّب طارئ إلى إعادة تسقيف كاملة، يبدأ كل مشروع بمنهجية التقييم الدقيقة نفسها.",
    items: [
      {
        title: "استبدال السقف بالكامل",
        intent: "سقفي بلغ نهاية عمره الافتراضي",
        body: "إزالة كاملة للسقف القديم وإعادة تسقيف بالنظام والطبقة التحتية والتهوية وتفاصيل الفلاشينج المناسبة لمنزلك.",
      },
      {
        title: "إصلاح السقف وتشخيص التسرّب",
        intent: "لديّ تسرّب أو تلف ظاهر",
        body: "نفحص مسارات التسرّب الشائعة مثل الفلاشينج ونقاط الاختراق والتصريف قبل اقتراح نطاق الإصلاح.",
      },
      {
        title: "ألواح الشينجل الأسفلتية المعمارية",
        intent: "أريد سقفاً شائعاً ومجرَّباً",
        body: "خيار قوي للأسقف السكنية المائلة، بتشكيلة واسعة من المقاطع والألوان.",
      },
      {
        title: "سقف معدني بدرزات قائمة",
        intent: "أريد عمراً أطول ومظهراً عصرياً",
        body: "خيار متميّز للعمارة المعاصرة وطراز الساحل الغربي، بمثبّتات مخفية وخطوط أنيقة.",
      },
      {
        title: "أسقف مسطحة ومنخفضة الميل",
        intent: "سقفي مسطح أو منخفض الميل",
        body: "أنظمة أغشية تُحدَّد وفق التصريف والميل والتفاصيل عند الحواجز والقواعد ونقاط الانتقال.",
      },
      {
        title: "فحص السقف وصيانته",
        intent: "أريد أن أطمئن على حالة سقفي",
        body: "تقييم موثَّق لحالة السقف وأولويات الصيانة لدعم قرارات مدروسة.",
      },
    ],
  },
  comparison: {
    eyebrow: "أنظمة التسقيف",
    title: "أي سقف يناسب منزلك؟",
    body: "لا توجد مادة تسقيف واحدة هي الأفضل لكل منزل؛ فالنظام المناسب يعتمد على الميل والعمارة ودرجة التعرّض للعوامل الجوية والتصريف والميزانية والمتطلبات المحلية. إليك مقارنة عامة بين الخيارات الرئيسية.",
    columns: {
      application: "الاستخدام المثالي",
      character: "طابع التصميم",
      maintenance: "اعتبارات الصيانة",
      investment: "التكلفة النسبية",
      performance: "خصائص الأداء",
    },
    systems: [
      {
        name: "ألواح الشينجل الأسفلتية المعمارية",
        summary: "خيار سكني شائع وموثوق.",
        application: "الأسقف المائلة في معظم منازل لوور مينلاند.",
        character: "ملمس مجسّم؛ من التقليدي إلى الانتقالي.",
        maintenance: "فحص دوري؛ ومكافحة الطحالب مهمة في المناخ الساحلي.",
        investment: "متوسطة",
        performance: "نظام معروف وموثوق، بتوفر واسع للمنتجات وإصلاحات مستقبلية ميسّرة.",
      },
      {
        name: "معدن بدرزات قائمة",
        summary: "خيار متميّز من حيث العمر والتصميم.",
        application: "العمارة المعاصرة وطراز الساحل الغربي ذات المسطحات النظيفة.",
        character: "خطوط حادة ومستقيمة ذات طابع معماري.",
        maintenance: "صيانة روتينية قليلة؛ وجودة التفاصيل والتثبيت عامل حاسم.",
        investment: "أعلى",
        performance: "مثبّتات مخفية وألواح متصلة تصرّف الماء بكفاءة على الواجهات المعرّضة.",
      },
      {
        name: "غشاء مسطح ومنخفض الميل",
        summary: "عائلة الأنظمة المناسبة للمساحات منخفضة الميل.",
        application: "مساحات السقف المسطحة ومنخفضة الميل والأسطح والملاحق.",
        character: "حضور بصري هادئ: فالسقف هنا ليس عنصراً جمالياً بارزاً.",
        maintenance: "فحص المصارف والدرزات، مع إبقاء مسارات التصريف خالية.",
        investment: "تختلف حسب الغشاء والنظام",
        performance:
          "يعتمد الأداء إلى حد كبير على الميل نحو المصرف واختيار الغشاء وتفاصيل نقاط الانتقال.",
      },
    ],
    disclaimer:
      "إرشادات عامة فقط. يخضع اختيار المواد وتفاصيل التنفيذ لإرشادات RCABC الحالية ومتطلبات كولومبيا البريطانية والبلديات المعمول بها. ولا نقدّم ضمانات هندسية.",
  },
  why: {
    eyebrow: "لماذا رولين روفينغ؟",
    title: "قسم التسقيف في شركة إنشاءات",
    body: "تطبّق رولين روفينغ منهجية مجموعة رولين في إدارة المشاريع على أعمال الأسقف السكنية: تقييم دقيق أولاً، ثم نطاق عمل مكتوب، مع عناية خاصة بالتفاصيل التي يتطلبها المناخ الساحلي.",
    items: [
      {
        title: "التقييم قبل التوصية",
        body: "نوثّق ما نجده قبل أن نقترح نطاق العمل.",
      },
      {
        title: "نطاق عمل وتسعير شفافان",
        body: "يحدّد نطاق العمل المقترح ما يشمله وما يستثنيه والظروف التي قد تستدعي تعديلاً.",
      },
      {
        title: "عناية معمارية",
        body: "تُختار خطوط السقف واللون والمقطع بما يليق بالمبنى.",
      },
      {
        title: "تفاصيل الفلاشينج ونقاط الانتقال",
        body: "نقاط الانتقال من أكثر مواضع الفحص شيوعاً، لذا تحظى بعناية خاصة أثناء التقييم وتحديد نطاق العمل.",
      },
      {
        title: "تخطيط الموقع",
        body: "نحدّد مسبقاً ترتيبات الوصول وحماية الممتلكات والتنظيف لكل عقار.",
      },
      {
        title: "التواصل خلال المشروع",
        body: "يُعتمد الجدول الزمني وآلية التواصل مع نطاق عمل المشروع.",
      },
      {
        title: "محطات ضبط الجودة",
        body: "فحوصات محددة أثناء التركيب وعند اكتماله.",
      },
      {
        title: "خبرة الشركة الأم",
        body: "تظل خبرات مجموعة رولين الإنشائية الأوسع في متناولك عندما لا تقتصر المشكلة على السقف وحده.",
      },
    ],
  },
  beforeAfter: {
    eyebrow: "قبل وبعد",
    title: "لاحظ الفرق في التفاصيل",
    body: "اسحب المقبض لمقارنة صور الأسقف قبل التنفيذ وبعده في منازل مترو فانكوفر.",
    beforeLabel: "قبل",
    afterLabel: "بعد",
    sliderLabel: "شريط مقارنة قبل وبعد",
  },
  process: {
    eyebrow: "خطوات العمل",
    title: "كيف يسير مشروع السقف لدى رولين؟",
    steps: [
      {
        n: "01",
        title: "تقييم السقف",
        body: "نفحص السقف والفلاشينج والتصريف والمؤشرات الداخلية، ونوثّق الحالة.",
      },
      {
        n: "02",
        title: "نطاق العمل والتوصية",
        body: "يمكن أن يوثّق العرض الخيارات المناسبة ومبرراتها ونطاق العمل والتسعير لمراجعتك.",
      },
      {
        n: "03",
        title: "التركيب / الإصلاح",
        body: "بعد الموافقة، يسير العمل وفق نطاق العمل الموثَّق وخطة الموقع ومتطلبات المشروع.",
      },
      {
        n: "04",
        title: "الفحص النهائي والتسليم",
        body: "تُستكمل متطلبات الإنجاز ووثائق المشروع وفق نطاق العمل المتفق عليه.",
      },
    ],
  },
  weather: {
    eyebrow: "ظروف كولومبيا البريطانية",
    title: "السقف هنا نظام لإدارة المياه",
    body: "في لوور مينلاند، يقضي السقف معظم السنة في التعامل مع أمطار طويلة لا مع الشمس، وهذا يغيّر الأولويات.",
    items: [
      {
        title: "أمطار طويلة ورطوبة",
        body: "في المناخ الساحلي الرطب، تزداد أهمية الطبقة التحتية والتراكبات والتهوية.",
      },
      {
        title: "الرياح والتعرّض",
        body: "تؤثر الواجهات المعرّضة والمواقع المكشوفة في طريقة التثبيت وتفاصيل الحواف.",
      },
      {
        title: "التصريف",
        body: "الميل نحو المصرف والمزاريب وسعة أنابيب التصريف تحدّد وجهة الماء عند اشتداد المطر.",
      },
      {
        title: "الثلوج حيثما وُجدت",
        body: "تشهد المناطق المرتفعة أحمال ثلوج ودورات ذوبان لا تعرفها المواقع المنخفضة.",
      },
      {
        title: "هندسة السقف",
        body: "الأودية والنوافذ الناتئة والمناور والملاحق تخلق نقاط انتقال تتطلب أكبر قدر من العناية.",
      },
    ],
  },
  areas: {
    eyebrow: "مناطق الخدمة",
    title: "نخدم مترو فانكوفر ولوور مينلاند",
    body: "إذا كان حيّك قريباً وغير مدرج في القائمة، تواصل معنا لنؤكد لك إمكانية الخدمة.",
  },
  faq: {
    eyebrow: "الأسئلة الشائعة",
    title: "أسئلة أصحاب المنازل",
    items: [
      {
        q: "هل يكفي الإصلاح أم أحتاج إلى استبدال السقف بالكامل؟",
        a: "يعتمد ذلك على عمر السقف وحالته وما إذا كانت المشكلة محصورة. فالعطل الموضعي قد يكفي فيه إصلاح محدد النطاق، بينما قد يستدعي الغطاء المتقادم أو تعدد نقاط الخلل التفكير في الاستبدال. ويساعد التقييم على توثيق الخيارات الملائمة للحالة الفعلية.",
      },
      {
        q: "سقفي يتسرّب. ماذا أفعل أولاً؟",
        a: "احتوِ الماء داخل المنزل، وصوّر ما تستطيع رؤيته بأمان، ثم اتصل بنا. ولا تصعد إلى سقف مبتل. كثيراً ما يبدأ التسرّب بعيداً عن البقعة الظاهرة، لذا فالتشخيص أهم من الترقيع المؤقت.",
      },
      {
        q: "ماذا يحدث أثناء تقييم السقف؟",
        a: "نراجع غطاء السقف والفلاشينج ونقاط الاختراق والتهوية والتصريف، ونتحقق من المؤشرات الداخلية عند الحاجة، ثم نوثّق النتائج. بعدها تتسلّم نطاق عمل مكتوباً وتوصية واضحة.",
      },
      {
        q: "ما أفضل مادة تسقيف؟",
        a: "لا توجد مادة هي الأفضل لكل منزل؛ فالميل والعمارة والتعرّض والتصريف والميزانية والمتطلبات المحلية كلها عوامل مؤثرة. نوضح لك الموازنات ونوصي بالنظام الأنسب بدل الترويج لمنتج واحد.",
      },
      {
        q: "هل تعملون على الأسقف المسطحة ومنخفضة الميل؟",
        a: "نعم. تُحدَّد المساحات منخفضة الميل كأنظمة أغشية، مع عناية خاصة بالميل نحو المصرف والتفاصيل عند الحواجز والقواعد ونقاط الانتقال.",
      },
      {
        q: "المعدن أم الشينجل؟",
        a: "ألواح الشينجل الأسفلتية المعمارية خيار سكني شائع وموثوق، والمعدن بدرزات قائمة خيار متميّز من حيث العمر والتصميم يناسب العمارة المعاصرة وطراز الساحل الغربي. كلاهما قد يكون الخيار الصحيح - فالأمر يعتمد على المبنى وأولوياتك.",
      },
      {
        q: "كم يستغرق مشروع التسقيف؟",
        a: "يعتمد الجدول الزمني على المساحة والتعقيد وسهولة الوصول والنظام المختار. نزوّدك بالمدة المتوقعة مع نطاق العمل، ونبلغك إذا تغيّرت الظروف.",
      },
      {
        q: "كيف أحصل على عرض سعر؟",
        a: "اطلب تقييماً للسقف؛ نعاين السقف ثم نقدّم نطاق عمل مكتوباً وسعراً محدداً. ولا نسعّر استبدال سقف من دون معاينته.",
      },
      {
        q: "ماذا لو أمطرت أثناء مشروعي؟",
        a: "قد يؤثر الطقس في سلامة العمل وتسلسله وجدوله. وتُدار إجراءات الحماية وأي تعديلات مرتبطة بالطقس من خلال خطة المشروع وآلية التواصل الخاصة به.",
      },
      {
        q: "ما الضمان المطبق؟",
        a: "تختلف تغطية الشركة المصنّعة باختلاف المنتج، وتُثبَّت شروط ضمان التنفيذ في نطاق عملك المكتوب. لا ننشر شروط الضمان هنا: فالشروط السارية على مشروعك هي الواردة في وثائقه.",
      },
      {
        q: "ما المناطق التي تخدمونها؟",
        a: "مترو فانكوفر ولوور مينلاند، بما في ذلك المناطق المدرجة في هذه الصفحة. تواصل معنا للتأكد من تغطية منطقتك.",
      },
    ],
  },
  form: {
    eyebrow: "اطلب تقييماً",
    title: "اطلب تقييم السقف",
    body: "حدّثنا عن العقار والسقف وما لاحظته.",
    name: "الاسم الكامل",
    phone: "الهاتف",
    email: "البريد الإلكتروني",
    postal: "الرمز البريدي",
    need: "نوع الخدمة المطلوبة",
    needOptions: [
      "تسرّب طارئ / دخول نشط للمياه",
      "إصلاح السقف / الفلاشينج",
      "استبدال ألواح الشينجل الأسفلتية",
      "سقف معدني",
      "سقف مسطح أو منخفض الميل",
      "سقف من خشب الأرز",
      "مزاريب / ألواح الواجهة / تهوية",
      "إزالة الطحالب / صيانة",
      "فحص حالة السقف",
      "لست متأكداً بعد",
    ],
    timeline: "التوقيت المفضل",
    timelineOptions: ["في أقرب وقت ممكن", "خلال ١–٣ أشهر", "٣–٦ أشهر", "مجرد تخطيط حالياً"],
    message: "وصف المشكلة (اختياري)",
    submit: "اطلب تقييم السقف",
    submitting: "جارٍ الإرسال…",
    successTitle: "تم استلام طلبك",
    successBody: "شكراً لك. وصلنا طلب تقييم السقف بنجاح.",
    errorRequired: "هذا الحقل مطلوب",
    errorEmail: "أدخل بريداً إلكترونياً صالحاً",
    errorPhone: "أدخل رقم هاتف صالحاً",
    errorSubmit: "تعذّر إرسال طلبك. يُرجى استخدام بيانات الاتصال في هذه الصفحة.",
    errorUnavailable:
      "الطلبات عبر الإنترنت غير متاحة مؤقتاً. يُرجى استخدام بيانات الاتصال في هذه الصفحة.",
    privacy: "بإرسال هذا النموذج، توافق على أن نتواصل معك بشأن طلبك المتعلق بالسقف.",
    privacyLink: "سياسة الخصوصية",
  },
  finalCta: {
    title: "سقفك يحمي كل ما تحته.",
    body: "ابدأ بالتقييم لتعرف حالة سقفك والخيارات المتاحة وما يتضمنه كل خيار.",
  },
  footer: {
    servicesTitle: "الخدمات",
    areasTitle: "مناطق الخدمة",
    contactTitle: "تواصل معنا",
    privacy: "الخصوصية",
    terms: "شروط الاستخدام",
    rights: "جميع الحقوق محفوظة.",
    disclaimer:
      "المعلومات المتعلقة بالتسقيف في هذا الموقع معلومات عامة وليست رأياً هندسياً، وتُعتمد التوصيات بعد تقييم ميداني.",
  },
  sticky: {
    call: "اتصل",
    quote: "اطلب عرض سعر",
  },
};
